import {
  Container,
  TtfTextSceneObject,
  Color4,
  TtfRenderComponent,
  EffectType,
  RenderEffect,
  ComplexRenderConfig,
  SceneObject,
  ASSETS_URL_FORMATTER_TOKEN,
  IAssetsUrlFormatter,
} from '@cgs/wge';
import { LocalizationManager, LOCALIZATION_MANAGER_TOKEN } from '@cgs/shared';
import { getCheatUIStyles } from './styles';
import { ResourcesComponent } from '../../../game/components/resources_component';
import { RESOURCES_COMPONENT_TOKEN } from '../../../type_definitions';
import { LAUNCH_PARAMS_STORE_TOKEN, LaunchParamsStore } from '../../../store';
import {
  TIME_SCALE_COMPONENT_TOKEN,
  TimeScaleComponent,
} from '../../../game/components/time-scale.component';

export const TTF_EDITOR_POPUP_TOKEN = Symbol('TtfEditorPopup');

const POSITION_STORAGE_KEY = 'ttf_editor_panel_position';

type GlowStrokeConfig = {
  enabled?: boolean;
  color?: string | Color4;
  width?: number;
  softness?: number;
  opacity?: number;
};

type ShadowConfig = {
  enabled?: boolean;
  color?: string | Color4;
  offsetX?: number;
  offsetY?: number;
  softness?: number;
  opacity?: number;
};

type GradientStopConfig = {
  offset?: number;
  color?: string;
};

type GradientFillConfig = {
  enabled?: boolean;
  type?: 'linear';
  angle?: number;
  offset?: number;
  stops?: GradientStopConfig[];
};

type EffectConfig = {
  id: string;
  type: EffectType;
  enabled?: boolean;
  order: number;
  color?: string;
  config?: GradientFillConfig | GlowStrokeConfig | ShadowConfig;
};

type ComplexRenderConfigLocal = {
  effects: EffectConfig[];
  letterSpacing?: number;
  lineSpacing?: number;
  canvasScaleX?: number;
  canvasScaleY?: number;
};

type RenderConfig = {
  fillColor?: string;
  fillGradient?: GradientFillConfig;
  outerGlow?: GlowStrokeConfig;
  innerGlow?: GlowStrokeConfig;
  shadow?: ShadowConfig;
  letterSpacing?: number;
  lineSpacing?: number;
  canvasScaleX?: number;
  canvasScaleY?: number;
  complexEffects?: ComplexRenderConfigLocal;
  dimensionSource?: string;
  dimensionSourceScale?: string;
  paddingTop?: number;
  paddingRight?: number;
  paddingBottom?: number;
  paddingLeft?: number;
};

export class TtfEditorPopupParams {
  languages: string[];
}

export class TtfEditorPopup {
  private rootEl: HTMLDivElement | null = null;
  private isShown: boolean = false;
  private isCollapsed: boolean = false;
  private savedTimeScale: number | null = null;

  private ttfSceneObjects: Set<TtfTextSceneObject>;
  private filteredObjects: TtfTextSceneObject[] = [];
  private selectedIndices: Set<number> = new Set();
  private sceneFilter: string = 'all';

  private currentTab: 'legacy' | 'effects' = 'legacy';
  private currentEffects: EffectConfig[] = [];
  private selectedEffectIndex: number = -1;
  private lastSelectedObjectId: string | null = null;

  // Localization fields
  private localizationManager: LocalizationManager | null = null;
  private currentLang: string = 'en';
  private availableLanguages: string[] = [];
  private readonly assetsUrlFormatter: IAssetsUrlFormatter | null = null;

  // Session store: key = "sceneId:ttfId", value = { legacy, complex } render configs
  private sessionStore: Map<string, { legacy?: RenderConfig; complex?: any }> = new Map();

  constructor(
    private readonly container: Container,
    private readonly params?: Partial<TtfEditorPopupParams>
  ) {
    // Initialize localization support
    this.assetsUrlFormatter = container.resolve<IAssetsUrlFormatter>(ASSETS_URL_FORMATTER_TOKEN, {
      optional: true,
    });
    this.localizationManager = container.resolve<LocalizationManager>(LOCALIZATION_MANAGER_TOKEN, {
      optional: true,
    });
    if (this.localizationManager) {
      this.currentLang = this.localizationManager.lang;
    }

    const launchParamsStore = container.resolve<LaunchParamsStore>(LAUNCH_PARAMS_STORE_TOKEN);

    if (launchParamsStore.state.cheatEnabled) {
      this.initialize();
    } else {
      const unsubscribe = launchParamsStore.stream.on((launchParams) => {
        if (!launchParams.cheatEnabled) {
          return;
        }

        this.initialize();

        unsubscribe();
      });
    }
  }

  private initialize(): void {
    const resourceComponent = this.container.resolve<ResourcesComponent>(RESOURCES_COMPONENT_TOKEN);
    this.ttfSceneObjects = new Set(resourceComponent.root.findAllByType(TtfTextSceneObject));

    SceneObject.initialized
      .filter((scene) => scene instanceof TtfTextSceneObject)
      .on((scene) => this.ttfSceneObjects.add(scene as TtfTextSceneObject));

    this.discoverAvailableLanguages();

    this.initializeUI();
  }

  private initializeUI(): void {
    this.rootEl = document.createElement('div');
    const styles = getCheatUIStyles();
    this.rootEl.style.cssText = styles.popup;
    this.restoreConsolePosition();

    this.rootEl.innerHTML = `
      <div class="cheat-header" style="${styles.headerStyle}">
        <span style="color: white; font-size: 14px;">TTF Text Editor</span>
        <div style="${styles.buttonContainerStyle}">
          <button id="ttfCollapseBtn" style="${styles.headerButtonStyle}" title="Collapse">\u2212</button>
          <button id="ttfCloseBtn" style="${styles.headerButtonStyle}" title="Close">\u00d7</button>
        </div>
      </div>
      <div id="ttf-editor-content" style="max-height: 80vh; overflow-y: auto; padding: 16px;"></div>
    `;

    document.body.appendChild(this.rootEl);

    try {
      this.rootEl.style.position = 'fixed';
      this.rootEl.style.top = '20px';
      this.rootEl.style.left = '20px';
      this.rootEl.style.right = 'auto';
      this.rootEl.style.bottom = 'auto';
      this.rootEl.style.transform = 'none';
      this.rootEl.style.width = '670px';
      this.rootEl.style.minWidth = '610px';
      this.rootEl.style.maxWidth = '80vw';
      this.rootEl.style.maxHeight = '85vh';
      this.rootEl.style.zIndex = '2147483647';
      this.rootEl.style.overflow = 'visible';
      this.rootEl.style.pointerEvents = 'auto';
    } catch {
      //
    }

    const contentEl = this.rootEl.querySelector<HTMLDivElement>('#ttf-editor-content');
    if (contentEl) {
      Object.assign(contentEl.style, {
        color: '#ffffff',
        backgroundColor: 'rgba(0,0,0,0.2)',
        fontFamily: 'Arial, sans-serif',
        fontSize: '14px',
        lineHeight: '1.35',
      } as CSSStyleDeclaration);
    }

    this.initializeDrag();
    this.setupEventListeners();
    this.renderUI();
    this.setShowState(false);

    document.addEventListener('keydown', (e) => this.handleKeyDown(e));
  }

  private initializeDrag(): void {
    if (!this.rootEl) return;
    const header = this.rootEl.querySelector('.cheat-header');
    if (!header) return;

    let isDragging = false;
    let startX = 0;
    let startY = 0;

    const onMouseDown = ((e: Event) => {
      const mouseEvent = e as MouseEvent;
      isDragging = true;
      startX = mouseEvent.clientX - this.rootEl!.offsetLeft;
      startY = mouseEvent.clientY - this.rootEl!.offsetTop;
      this.rootEl!.style.cursor = 'grabbing';
    }) as EventListener;

    const onMouseMove = ((e: Event) => {
      if (!isDragging || !this.rootEl) return;
      const mouseEvent = e as MouseEvent;
      const x = mouseEvent.clientX - startX;
      const y = mouseEvent.clientY - startY;
      this.rootEl.style.left = `${x}px`;
      this.rootEl.style.top = `${y}px`;
      this.rootEl.style.transform = 'none';
    }) as EventListener;

    const onMouseUp = (() => {
      if (isDragging) {
        isDragging = false;
        if (this.rootEl) {
          this.rootEl.style.cursor = 'default';
          this.saveConsolePosition();
        }
      }
    }) as EventListener;

    header.addEventListener('mousedown', onMouseDown);
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }

  private setupEventListeners(): void {
    if (!this.rootEl) return;
    this.rootEl
      .querySelector('#ttfCollapseBtn')
      ?.addEventListener('click', () => this.toggleCollapse());
    this.rootEl
      .querySelector('#ttfCloseBtn')
      ?.addEventListener('click', () => this.toggleShownState());
  }

  private toggleCollapse(): void {
    this.isCollapsed = !this.isCollapsed;
    const content = this.rootEl?.querySelector<HTMLDivElement>('#ttf-editor-content');
    if (content) {
      content.style.display = this.isCollapsed ? 'none' : 'block';
      const collapseBtn = this.rootEl?.querySelector<HTMLButtonElement>('#ttfCollapseBtn');
      if (collapseBtn) collapseBtn.textContent = this.isCollapsed ? '+' : '\u2212';
    }
  }

  private toggleShownState(): void {
    this.setShowState(!this.isShown);
  }

  private setShowState(show: boolean): void {
    const wasShown = this.isShown;
    this.isShown = show;
    if (this.rootEl) this.rootEl.style.display = show ? 'block' : 'none';
    const timeScaleComponent = this.container.resolve<TimeScaleComponent>(
      TIME_SCALE_COMPONENT_TOKEN
    );
    if (show && !wasShown) {
      this.savedTimeScale = timeScaleComponent.timeScale;
      timeScaleComponent.timeScale = 0.0001;
    } else if (!show && wasShown) {
      timeScaleComponent.timeScale = this.savedTimeScale ?? 1;
      this.savedTimeScale = null;
    }

    if (show) {
      this.refreshObjects();
      this.renderUI();
    }
  }

  private saveConsolePosition(): void {
    if (!this.rootEl) return;
    const rect = this.rootEl.getBoundingClientRect();
    localStorage.setItem(POSITION_STORAGE_KEY, JSON.stringify({ x: rect.left, y: rect.top }));
  }

  private restoreConsolePosition(): void {
    if (!this.rootEl) return;
    const saved = localStorage.getItem(POSITION_STORAGE_KEY);
    if (saved) {
      try {
        const { x, y } = JSON.parse(saved);
        this.rootEl.style.left = `${x}px`;
        this.rootEl.style.top = `${y}px`;
        this.rootEl.style.transform = 'none';
      } catch {
        //
      }
    }
  }

  private handleKeyDown(event: KeyboardEvent): void {
    if (event.key === 't' || event.key === 'T') {
      this.toggleShownState();
    }
  }

  private discoverAvailableLanguages(): void {
    const folderPath = 'translates';
    const baseUrl = this.assetsUrlFormatter?.format(folderPath) || folderPath;

    const checks = (this.params?.languages || []).map((lang) =>
      fetch(`${baseUrl}/localization.${lang}.json`, { method: 'HEAD' })
        .then((r) => (r.ok ? lang : null))
        .catch(() => null)
    );

    Promise.all(checks).then((results) => {
      this.availableLanguages = results.filter((l): l is string => l !== null).sort();
      if (this.isShown) {
        this.updateLanguageSelect();
      }
    });
  }

  private updateLanguageSelect(): void {
    const select = this.rootEl?.querySelector<HTMLSelectElement>('#ttf-lang-select');
    if (!select) return;
    select.innerHTML = '';
    this.availableLanguages.forEach((lang) => {
      const opt = document.createElement('option');
      opt.value = lang;
      opt.text = lang.toUpperCase();
      if (lang === this.currentLang) opt.selected = true;
      select.appendChild(opt);
    });
  }

  private async switchLanguage(lang: string): Promise<void> {
    if (lang === this.currentLang && this.localizationManager) return;

    this.currentLang = lang;
    const newManager = new LocalizationManager(this.container, lang);
    await newManager.init();
    this.localizationManager = newManager;

    this.ttfSceneObjects.forEach((ttf) => {
      this.applyLocalizationToObject(ttf);
    });

    if (this.isShown) {
      this.renderUI();
    }
  }

  private applyLocalizationToObject(ttf: TtfTextSceneObject): void {
    if (!this.localizationManager || !ttf.id) return;
    const text = this.localizationManager.get(ttf.id);
    if (text) {
      ttf.text = text;
      ttf.textRenderParams.lang = this.currentLang;
    }
  }

  private getLocalizationText(ttfId: string): string | null {
    if (!this.localizationManager || !ttfId) return null;
    const text = this.localizationManager.get(ttfId);
    return text || null;
  }

  private refreshObjects(): void {
    const scenes = new Set<string>();
    this.ttfSceneObjects.forEach((o) => {
      if (o.sceneUrl) scenes.add(o.sceneUrl);
    });
    if (this.sceneFilter === 'all') {
      this.filteredObjects = Array.from(this.ttfSceneObjects);
    } else {
      this.filteredObjects = Array.from(this.ttfSceneObjects).filter(
        (o) => (o.sceneUrl || '') === this.sceneFilter
      );
    }
    // Remove indices that are out of bounds after filtering
    for (const idx of Array.from(this.selectedIndices)) {
      if (idx >= this.filteredObjects.length) {
        this.selectedIndices.delete(idx);
      }
    }
  }

  private renderUI(): void {
    const content = this.rootEl?.querySelector<HTMLDivElement>('#ttf-editor-content');
    if (!content) return;
    content.innerHTML = '';

    // --- Toolbar row ---
    const toolbar = document.createElement('div');
    toolbar.style.cssText =
      'display: flex; align-items: center; gap: 8px; margin-bottom: 12px; flex-wrap: wrap;';

    const refreshBtn = document.createElement('button');
    refreshBtn.textContent = 'Refresh';
    refreshBtn.onclick = () => {
      this.refreshObjects();
      this.renderUI();
    };
    try {
      refreshBtn.style.cssText += getCheatUIStyles().buttonStyle;
    } catch {
      //
    }
    toolbar.appendChild(refreshBtn);

    // Scene filter
    const scenes = Array.from(
      new Set(Array.from(this.ttfSceneObjects).map((o) => o.sceneUrl || ''))
    ).sort();
    const sceneSelect = document.createElement('select');
    sceneSelect.style.cssText =
      'height: 32px; border-radius: 6px; padding: 4px 8px; background: #3a3a3a; color: #fff; border: 1px solid #555;';
    const optAll = document.createElement('option');
    optAll.value = 'all';
    optAll.text = 'All scenes';
    sceneSelect.appendChild(optAll);
    scenes.forEach((s) => {
      const opt = document.createElement('option');
      opt.value = s || '';
      opt.text = s || '(no scene)';
      sceneSelect.appendChild(opt);
    });
    sceneSelect.value = this.sceneFilter;
    sceneSelect.onchange = () => {
      this.sceneFilter = sceneSelect.value;
      this.refreshObjects();
      this.renderUI();
    };
    toolbar.appendChild(sceneSelect);

    // Language dropdown
    const langLabel = document.createElement('span');
    langLabel.textContent = 'Lang:';
    langLabel.style.cssText = 'color: #aaa; font-size: 12px; margin-left: 8px;';
    toolbar.appendChild(langLabel);

    const langSelect = document.createElement('select');
    langSelect.id = 'ttf-lang-select';
    langSelect.style.cssText =
      'height: 32px; border-radius: 6px; padding: 4px 8px; background: #3a3a3a; color: #fff; border: 1px solid #555;';
    if (this.availableLanguages.length > 0) {
      this.availableLanguages.forEach((lang) => {
        const opt = document.createElement('option');
        opt.value = lang;
        opt.text = lang.toUpperCase();
        if (lang === this.currentLang) opt.selected = true;
        langSelect.appendChild(opt);
      });
    } else {
      const opt = document.createElement('option');
      opt.value = this.currentLang;
      opt.text = this.currentLang.toUpperCase();
      langSelect.appendChild(opt);
    }
    langSelect.onchange = () => {
      this.switchLanguage(langSelect.value);
    };
    toolbar.appendChild(langSelect);

    // Select all / Deselect all buttons
    const selectAllBtn = document.createElement('button');
    selectAllBtn.textContent = 'Select All';
    selectAllBtn.style.cssText =
      'height: 32px; padding: 4px 10px; border-radius: 6px; background: #3a5a3a; color: #fff; border: 1px solid #555; cursor: pointer; font-size: 12px; margin-left: auto;';
    selectAllBtn.onclick = () => {
      this.selectedIndices = new Set(this.filteredObjects.map((_, i) => i));
      this.renderUI();
    };
    toolbar.appendChild(selectAllBtn);

    const deselectBtn = document.createElement('button');
    deselectBtn.textContent = 'Deselect';
    deselectBtn.style.cssText =
      'height: 32px; padding: 4px 10px; border-radius: 6px; background: #5a3a3a; color: #fff; border: 1px solid #555; cursor: pointer; font-size: 12px;';
    deselectBtn.onclick = () => {
      this.selectedIndices.clear();
      this.renderUI();
    };
    toolbar.appendChild(deselectBtn);

    content.appendChild(toolbar);

    // --- TimeScale slider ---
    const timeScaleComponent = this.container.resolve<TimeScaleComponent>(
      TIME_SCALE_COMPONENT_TOKEN
    );
    const timeScaleRow = document.createElement('div');
    timeScaleRow.style.cssText =
      'display: flex; align-items: center; gap: 8px; margin-bottom: 12px; padding: 8px; background: #2a2a2a; border-radius: 6px;';

    const tsLabel = document.createElement('label');
    tsLabel.textContent = 'TimeScale:';
    tsLabel.style.cssText = 'color: #fff; font-size: 12px; min-width: 70px;';
    timeScaleRow.appendChild(tsLabel);

    const tsSlider = document.createElement('input');
    tsSlider.type = 'range';
    tsSlider.min = '0';
    tsSlider.max = '4';
    tsSlider.step = '0.1';
    tsSlider.value = String(timeScaleComponent.timeScale);
    tsSlider.style.cssText = 'flex: 1; accent-color: #6a9bd1;';
    timeScaleRow.appendChild(tsSlider);

    const tsValue = document.createElement('span');
    tsValue.textContent = Number(tsSlider.value).toFixed(1);
    tsValue.style.cssText = 'color: #fff; font-size: 12px; min-width: 30px; text-align: right;';
    timeScaleRow.appendChild(tsValue);

    tsSlider.addEventListener('input', () => {
      const val = parseFloat(tsSlider.value);
      timeScaleComponent.timeScale = val;
      tsValue.textContent = val.toFixed(1);
    });

    content.appendChild(timeScaleRow);

    // --- Selection info ---
    const selectionInfo = document.createElement('div');
    selectionInfo.style.cssText =
      'display: flex; align-items: center; justify-content: space-between; margin-bottom: 6px;';
    const selLabel = document.createElement('span');
    selLabel.style.cssText = 'color: #aaa; font-size: 12px;';
    selLabel.textContent = `Objects: ${this.filteredObjects.length} | Selected: ${this.selectedIndices.size}`;
    selectionInfo.appendChild(selLabel);
    content.appendChild(selectionInfo);

    // --- Multi-select list ---
    const list = document.createElement('select');
    list.multiple = true;
    list.size = 8;
    list.style.cssText =
      'width: 100%; background: #1f1f1f; color: #fff; border: 1px solid #444; padding: 6px; border-radius: 6px;';
    this.filteredObjects.forEach((o, idx) => {
      const opt = document.createElement('option');
      const label = `${o.id || '(no-id)'}  |  ${o.sceneUrl || ''}`;
      opt.value = String(idx);
      opt.text = label;
      if (this.selectedIndices.has(idx)) opt.selected = true;
      list.appendChild(opt);
    });
    list.onchange = () => {
      this.selectedIndices.clear();
      for (const opt of Array.from(list.selectedOptions)) {
        this.selectedIndices.add(parseInt(opt.value, 10));
      }
      this.renderEditor(content);
    };
    content.appendChild(list);

    const editorHost = document.createElement('div');
    editorHost.id = 'ttf-editor-form';
    editorHost.style.marginTop = '12px';
    content.appendChild(editorHost);

    this.renderEditor(content);
  }

  private getSelectedObjects(): TtfTextSceneObject[] {
    return Array.from(this.selectedIndices)
      .filter((i) => i >= 0 && i < this.filteredObjects.length)
      .map((i) => this.filteredObjects[i]);
  }

  private getPrimarySelected(): TtfTextSceneObject | null {
    const indices = Array.from(this.selectedIndices).sort((a, b) => a - b);
    if (indices.length === 0) return null;
    return this.filteredObjects[indices[0]] ?? null;
  }

  private renderEditor(content: HTMLDivElement): void {
    const host = content.querySelector<HTMLDivElement>('#ttf-editor-form');
    if (!host) return;
    host.innerHTML = '';

    const current = this.getPrimarySelected();
    if (!current) {
      host.innerHTML =
        '<div style="color:#bbb">Select one or more TtfTextSceneObjects to edit...</div>';
      return;
    }

    if (this.selectedIndices.size > 1) {
      const multiInfo = document.createElement('div');
      multiInfo.style.cssText =
        'background: #2a3a5a; padding: 8px 12px; border-radius: 6px; margin-bottom: 12px; color: #8ab4f8; font-size: 12px;';
      multiInfo.textContent = `Editing ${this.selectedIndices.size} objects simultaneously. Changes apply to all selected.`;
      host.appendChild(multiInfo);
    }

    if (this.lastSelectedObjectId !== current.id) {
      this.currentEffects = [];
      this.selectedEffectIndex = -1;
      this.lastSelectedObjectId = current.id;
    }

    // --- Session bar ---
    const sessionBar = document.createElement('div');
    sessionBar.style.cssText =
      'display: flex; align-items: center; gap: 8px; margin-bottom: 12px; padding: 8px; background: #2a2a2a; border-radius: 6px; flex-wrap: wrap;';

    const sessionCount = document.createElement('span');
    sessionCount.style.cssText = `font-size: 11px; color: ${this.sessionStore.size > 0 ? '#8adb8a' : '#666'};`;
    sessionCount.textContent = `Session: ${this.sessionStore.size} item(s)`;
    sessionBar.appendChild(sessionCount);

    const logSessionBtn = document.createElement('button');
    logSessionBtn.textContent = 'Log Session';
    logSessionBtn.style.cssText =
      'padding: 6px 12px; background: #3a4a6a; color: #fff; border: 1px solid #4a6a8a; border-radius: 4px; cursor: pointer; font-size: 12px;';
    logSessionBtn.onclick = () => {
      this.logSessionToConsole();
      this.flashStatus(host, 'Session logged to console');
    };
    sessionBar.appendChild(logSessionBtn);

    const copySessionBtn = document.createElement('button');
    copySessionBtn.textContent = 'Copy Session';
    copySessionBtn.style.cssText =
      'padding: 6px 12px; background: #5a5a3a; color: #fff; border: 1px solid #7a7a4a; border-radius: 4px; cursor: pointer; font-size: 12px;';
    copySessionBtn.onclick = () => {
      const payload = this.buildSessionPayload();
      const json = JSON.stringify(payload, null, 2);
      this.copyToClipboard(json, host);
    };
    sessionBar.appendChild(copySessionBtn);

    const clearBtn = document.createElement('button');
    clearBtn.textContent = 'Clear Session';
    clearBtn.style.cssText =
      'padding: 6px 12px; background: #6a3a3a; color: #fff; border: 1px solid #8a4a4a; border-radius: 4px; cursor: pointer; font-size: 12px;';
    clearBtn.onclick = () => {
      this.clearSession();
      sessionCount.textContent = 'Session: 0 item(s)';
      sessionCount.style.color = '#666';
      this.flashStatus(host, 'Session cleared');
    };
    sessionBar.appendChild(clearBtn);

    // Copy JSON button (copies from active tab for current selection only)
    const copyBtn = document.createElement('button');
    copyBtn.textContent = 'Copy JSON';
    copyBtn.style.cssText =
      'padding: 6px 12px; background: #4a4a4a; color: #fff; border: 1px solid #666; border-radius: 4px; cursor: pointer; font-size: 12px; margin-left: auto;';
    copyBtn.onclick = () => {
      if (this.currentTab === 'legacy') {
        this.copyJsonFromForm(host);
      } else {
        this.copyComplexJsonFromForm(host);
      }
    };
    sessionBar.appendChild(copyBtn);

    host.appendChild(sessionBar);

    // --- Tabs ---
    const tabContainer = document.createElement('div');
    tabContainer.style.cssText = 'display: flex; margin-bottom: 0; border-bottom: 2px solid #444;';

    const makeTab = (label: string, tabId: 'legacy' | 'effects') => {
      const tab = document.createElement('button');
      tab.textContent = label;
      const isActive = this.currentTab === tabId;
      tab.style.cssText = `
        padding: 8px 20px; background: ${isActive ? '#444' : '#2a2a2a'};
        color: ${isActive ? '#fff' : '#888'}; border: 1px solid ${isActive ? '#555' : '#333'};
        border-bottom: ${isActive ? '2px solid #6a9bd1' : '2px solid transparent'};
        cursor: pointer; border-radius: 4px 4px 0 0; margin-right: 2px;
        font-size: 13px; font-weight: ${isActive ? 'bold' : 'normal'};
        transition: all 0.15s;
      `;
      tab.onclick = () => {
        // Snapshot current tab values before switching
        this.snapshotCurrentTab(current);
        this.currentTab = tabId;
        if (tabId === 'effects') {
          this.loadCurrentEffectsFromSession(current);
        }
        this.renderEditor(content);
      };
      return tab;
    };

    tabContainer.appendChild(makeTab('Legacy Effects', 'legacy'));
    tabContainer.appendChild(makeTab('Complex Effects', 'effects'));
    host.appendChild(tabContainer);

    // --- Tab content container ---
    const tabContent = document.createElement('div');
    tabContent.id = 'ttf-tab-content';
    tabContent.style.cssText = 'margin-top: 12px;';
    host.appendChild(tabContent);

    // Localization info section
    this.renderLocalizationSection(tabContent as HTMLDivElement, current);

    if (this.currentTab === 'legacy') {
      this.renderLegacyEditor(tabContent as HTMLDivElement, current);
    } else {
      this.renderEffectsEditor(tabContent as HTMLDivElement, current);
    }
  }

  private renderLocalizationSection(host: HTMLDivElement, current: TtfTextSceneObject): void {
    const section = document.createElement('div');
    section.style.cssText = `
      margin-bottom: 12px;
      padding: 10px;
      background: #1a2a1a;
      border: 1px solid #3a5a3a;
      border-radius: 6px;
    `;

    const header = document.createElement('div');
    header.style.cssText = `
      display: flex; align-items: center; justify-content: space-between;
      margin-bottom: 8px;
    `;
    const title = document.createElement('span');
    title.textContent = `Localization [${this.currentLang.toUpperCase()}]`;
    title.style.cssText = 'color: #9BE564; font-weight: bold; font-size: 13px;';
    header.appendChild(title);

    const idBadge = document.createElement('span');
    idBadge.textContent = `key: ${current.id || '(no id)'}`;
    idBadge.style.cssText = 'color: #888; font-size: 12px; font-family: monospace;';
    header.appendChild(idBadge);
    section.appendChild(header);

    // Current object text
    const currentTextLabel = document.createElement('div');
    currentTextLabel.style.cssText = 'color: #aaa; font-size: 11px; margin-bottom: 2px;';
    currentTextLabel.textContent = 'Current text:';
    section.appendChild(currentTextLabel);

    const currentTextValue = document.createElement('div');
    currentTextValue.style.cssText = `
      color: #fff; font-size: 13px; padding: 4px 6px;
      background: #2a2a2a; border-radius: 4px; margin-bottom: 8px;
      word-break: break-word; min-height: 20px;
    `;
    currentTextValue.textContent = current.text || '(empty)';
    section.appendChild(currentTextValue);

    // Localized text
    const localizedText = this.getLocalizationText(current.id);
    const locLabel = document.createElement('div');
    locLabel.style.cssText = 'color: #aaa; font-size: 11px; margin-bottom: 2px;';
    locLabel.textContent = `Localized text (${this.currentLang}):`;
    section.appendChild(locLabel);

    const locValue = document.createElement('div');
    locValue.style.cssText = `
      color: ${localizedText ? '#9BE564' : '#ff6b6b'}; font-size: 13px; padding: 4px 6px;
      background: #2a2a2a; border-radius: 4px; margin-bottom: 8px;
      word-break: break-word; min-height: 20px;
    `;
    locValue.textContent = localizedText || '(no translation for this key)';
    section.appendChild(locValue);

    if (localizedText) {
      const applyBtn = document.createElement('button');
      applyBtn.textContent = 'Apply Localized Text';
      applyBtn.style.cssText = `
        background: #3a5a3a; color: #fff; border: 1px solid #5a8a5a;
        padding: 4px 12px; border-radius: 4px; cursor: pointer; font-size: 12px;
        margin-right: 8px;
      `;
      applyBtn.onclick = () => {
        current.text = localizedText;
        current.textRenderParams.lang = this.currentLang;
        currentTextValue.textContent = localizedText;
      };
      section.appendChild(applyBtn);
    }

    const applyAllBtn = document.createElement('button');
    applyAllBtn.textContent = 'Apply Lang to All';
    applyAllBtn.style.cssText = `
      background: #3a3a5a; color: #fff; border: 1px solid #5a5a8a;
      padding: 4px 12px; border-radius: 4px; cursor: pointer; font-size: 12px;
    `;
    applyAllBtn.onclick = () => {
      this.ttfSceneObjects.forEach((ttf) => this.applyLocalizationToObject(ttf));
      currentTextValue.textContent = current.text || '(empty)';
      this.flashStatus(
        host,
        `Applied ${this.currentLang.toUpperCase()} translations to all objects`
      );
    };
    section.appendChild(applyAllBtn);

    host.appendChild(section);
  }

  private renderLegacyEditor(host: HTMLDivElement, current: TtfTextSceneObject): void {
    const form = document.createElement('div');
    form.style.display = 'grid';
    form.style.gridTemplateColumns = '140px 1fr';
    form.style.gap = '10px 10px';
    (form.style as any).alignItems = 'center';

    const addRow = (label: string, input: HTMLElement) => {
      const l = document.createElement('label');
      l.textContent = label;
      l.style.alignSelf = 'center';
      l.style.color = '#ffffff';
      form.appendChild(l);
      form.appendChild(input);
    };

    const textInput = (placeholder = '') => {
      const i = document.createElement('input');
      i.type = 'text';
      i.placeholder = placeholder;
      i.style.width = '100%';
      i.style.backgroundColor = '#3a3a3a';
      i.style.color = '#ffffff';
      i.style.border = '1px solid #555';
      i.style.height = '32px';
      i.style.padding = '4px 8px';
      i.style.borderRadius = '6px';
      return i;
    };

    const numberInput = (min?: number, max?: number, step?: number) => {
      const i = document.createElement('input');
      i.type = 'number';
      if (min !== undefined) i.min = String(min);
      if (max !== undefined) i.max = String(max);
      if (step !== undefined) i.step = String(step);
      i.style.width = '100%';
      i.style.backgroundColor = '#3a3a3a';
      i.style.color = '#ffffff';
      i.style.border = '1px solid #555';
      i.style.height = '32px';
      i.style.padding = '4px 8px';
      i.style.borderRadius = '6px';
      return i as HTMLInputElement;
    };

    const checkbox = () => {
      const i = document.createElement('input');
      i.type = 'checkbox';
      return i as HTMLInputElement;
    };

    const colorPair = (baseId: string, placeholder = '#RRGGBB or rgba') => {
      const wrap = document.createElement('div');
      wrap.style.display = 'grid';
      wrap.style.gridTemplateColumns = '90px 1fr';
      wrap.style.gap = '6px';

      const picker = document.createElement('input');
      picker.type = 'color';
      picker.id = `${baseId}-picker`;
      picker.style.width = '90px';
      picker.style.height = '32px';
      picker.style.border = '1px solid #555';
      picker.style.background = '#3a3a3a';
      picker.style.borderRadius = '6px';

      const text = textInput(placeholder) as HTMLInputElement;
      text.id = baseId;
      text.style.height = '32px';
      text.style.borderRadius = '6px';

      picker.addEventListener('input', () => {
        text.value = picker.value;
        this.applyFromForm(host);
      });

      const syncTextToPicker = (raw: string) => {
        const v = raw.trim();
        const hex = this.colorToHex(v);
        if (hex) picker.value = hex;
        this.applyFromForm(host);
      };

      text.addEventListener('input', () => syncTextToPicker(text.value));
      text.addEventListener('paste', (e: ClipboardEvent) => {
        const pasted = e.clipboardData?.getData('text');
        if (pasted) {
          e.preventDefault();
          text.value = pasted.trim();
          syncTextToPicker(text.value);
        }
      });

      wrap.appendChild(picker);
      wrap.appendChild(text);
      return wrap;
    };

    const fillPair = colorPair('rc-fillColor');
    addRow('Fill Color', fillPair);

    const gradEnabled = checkbox();
    gradEnabled.id = 'rc-grad-enabled';
    addRow('Gradient Enabled', gradEnabled);
    const gradAngle = numberInput(0, 360, 1);
    gradAngle.id = 'rc-grad-angle';
    addRow('Gradient Angle', gradAngle);
    const gradOffset = numberInput(-1, 1, 0.01);
    gradOffset.id = 'rc-grad-offset';
    addRow('Gradient Offset', gradOffset);
    const g0Wrap = colorPair('rc-grad-stop0-color', '#ff0000');
    addRow('Grad Stop0 Color', g0Wrap);
    const g0Off = numberInput(0, 1, 0.01);
    g0Off.id = 'rc-grad-stop0-offset';
    addRow('Grad Stop0 Offset', g0Off);
    const g1Wrap = colorPair('rc-grad-stop1-color', '#0000ff');
    addRow('Grad Stop1 Color', g1Wrap);
    const g1Off = numberInput(0, 1, 0.01);
    g1Off.id = 'rc-grad-stop1-offset';
    addRow('Grad Stop1 Offset', g1Off);

    const ogEnabled = checkbox();
    ogEnabled.id = 'rc-og-enabled';
    addRow('OuterGlow Enabled', ogEnabled);
    const ogColor = colorPair('rc-og-color', '#ffffff');
    addRow('OuterGlow Color', ogColor);
    const ogWidth = numberInput(0, 100, 1);
    ogWidth.id = 'rc-og-width';
    addRow('OuterGlow Width', ogWidth);
    const ogSoft = numberInput(0, 100, 1);
    ogSoft.id = 'rc-og-softness';
    addRow('OuterGlow Softness', ogSoft);
    const ogOp = numberInput(0, 1, 0.05);
    ogOp.id = 'rc-og-opacity';
    addRow('OuterGlow Opacity', ogOp);

    const igEnabled = checkbox();
    igEnabled.id = 'rc-ig-enabled';
    addRow('InnerGlow Enabled', igEnabled);
    const igColor = colorPair('rc-ig-color', '#ffffff');
    addRow('InnerGlow Color', igColor);
    const igWidth = numberInput(0, 100, 1);
    igWidth.id = 'rc-ig-width';
    addRow('InnerGlow Width', igWidth);
    const igSoft = numberInput(0, 100, 1);
    igSoft.id = 'rc-ig-softness';
    addRow('InnerGlow Softness', igSoft);
    const igOp = numberInput(0, 1, 0.05);
    igOp.id = 'rc-ig-opacity';
    addRow('InnerGlow Opacity', igOp);

    const shEnabled = checkbox();
    shEnabled.id = 'rc-sh-enabled';
    addRow('Shadow Enabled', shEnabled);
    const shColor = colorPair('rc-sh-color', 'rgba(0,0,0,0.5)');
    addRow('Shadow Color', shColor);
    const shOX = numberInput(-100, 100, 1);
    shOX.id = 'rc-sh-offsetX';
    addRow('Shadow offsetX', shOX);
    const shOY = numberInput(-100, 100, 1);
    shOY.id = 'rc-sh-offsetY';
    addRow('Shadow offsetY', shOY);
    const shSoft = numberInput(0, 100, 1);
    shSoft.id = 'rc-sh-softness';
    addRow('Shadow Softness', shSoft);
    const shOp = numberInput(0, 1, 0.05);
    shOp.id = 'rc-sh-opacity';
    addRow('Shadow Opacity', shOp);

    const ls = numberInput(-100, 100, 0.1);
    ls.id = 'rc-letterSpacing';
    addRow('Letter Spacing', ls);
    const lineSpacing = numberInput(-100, 100, 0.1);
    lineSpacing.id = 'rc-lineSpacing';
    addRow('Line Spacing', lineSpacing);
    const scx = numberInput(0, 10, 0.01);
    scx.id = 'rc-scaleX';
    addRow('Scale X', scx);
    const scy = numberInput(0, 10, 0.01);
    scy.id = 'rc-scaleY';
    addRow('Scale Y', scy);

    const autoscaleEnabled = checkbox();
    autoscaleEnabled.id = 'rc-autoscale-enabled';
    addRow('Autoscale Enabled', autoscaleEnabled);

    const dimensionSourceSelect = this.createDimensionSourceSelect();
    dimensionSourceSelect.id = 'rc-dimension-source';
    addRow('Dimension Source', dimensionSourceSelect);

    const dimensionScaleSelect = this.createDimensionScaleSelect();
    dimensionScaleSelect.id = 'rc-dimension-scale';
    addRow('Dimension Scale', dimensionScaleSelect);

    const paddingTop = numberInput(0, 100, 1);
    paddingTop.id = 'rc-padding-top';
    addRow('Padding Top', paddingTop);
    const paddingRight = numberInput(0, 100, 1);
    paddingRight.id = 'rc-padding-right';
    addRow('Padding Right', paddingRight);
    const paddingBottom = numberInput(0, 100, 1);
    paddingBottom.id = 'rc-padding-bottom';
    addRow('Padding Bottom', paddingBottom);
    const paddingLeft = numberInput(0, 100, 1);
    paddingLeft.id = 'rc-padding-left';
    addRow('Padding Left', paddingLeft);

    form.addEventListener('input', () => this.applyFromForm(host));

    host.appendChild(form);

    // Priority: session.legacy -> session.complex -> object config -> defaults
    const sessionEntry = this.getSessionEntry(current);
    if (sessionEntry?.legacy) {
      this.populateFormFromRenderConfig(host, sessionEntry.legacy);
    } else if (sessionEntry?.complex) {
      this.populateFormFromRenderConfig(host, sessionEntry.complex);
    } else {
      this.populateFormFromObject(host, current);
    }

    // Apply restored values to objects
    this.applyFromForm(host);
  }

  private renderEffectsEditor(host: HTMLDivElement, current: TtfTextSceneObject): void {
    const container = document.createElement('div');
    container.style.cssText = `
      display: flex;
      gap: 20px;
      height: 400px;
      font-family: Arial, sans-serif;
    `;

    const leftPanel = document.createElement('div');
    leftPanel.style.cssText = `
      flex: 0 0 250px;
      display: flex;
      flex-direction: column;
      background: #2a2a2a;
      border: 1px solid #444;
      border-radius: 6px;
      padding: 12px;
    `;

    const listHeader = document.createElement('div');
    listHeader.style.cssText = `
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 12px;
      padding-bottom: 8px;
      border-bottom: 1px solid #444;
    `;

    const effectsTitle = document.createElement('h4');
    effectsTitle.textContent = 'Effects';
    effectsTitle.style.cssText = 'color: #ffffff; margin: 0; font-size: 14px; font-weight: bold;';

    const effectsCount = document.createElement('span');
    effectsCount.textContent = `(${this.currentEffects.length})`;
    effectsCount.style.cssText = 'color: #888; font-size: 12px;';

    listHeader.appendChild(effectsTitle);
    listHeader.appendChild(effectsCount);
    leftPanel.appendChild(listHeader);

    const addEffectContainer = document.createElement('div');
    addEffectContainer.style.cssText =
      'display: flex; flex-direction: column; gap: 6px; margin-bottom: 12px;';

    const addEffectLabel = document.createElement('div');
    addEffectLabel.textContent = 'Add Effect:';
    addEffectLabel.style.cssText = 'color: #ccc; font-size: 12px; font-weight: bold;';
    addEffectContainer.appendChild(addEffectLabel);

    const effectTypes = [
      { value: EffectType.Fill, label: 'Fill' },
      { value: EffectType.Gradient, label: 'Gradient' },
      { value: EffectType.OuterGlow, label: 'Outer Glow' },
      { value: EffectType.InnerGlow, label: 'Inner Glow' },
      { value: EffectType.Shadow, label: 'Shadow' },
    ];

    effectTypes.forEach((type) => {
      const btn = document.createElement('button');
      btn.textContent = type.label;
      btn.style.cssText = `
        background: #3a3a3a; color: #ffffff; border: 1px solid #555;
        padding: 6px 10px; border-radius: 4px; cursor: pointer;
        font-size: 12px; transition: all 0.2s; text-align: left;
      `;
      btn.onmouseover = () => (btn.style.background = '#4a4a4a');
      btn.onmouseout = () => (btn.style.background = '#3a3a3a');
      btn.onclick = () => this.addNewEffect(host, current, type.value);
      addEffectContainer.appendChild(btn);
    });

    leftPanel.appendChild(addEffectContainer);

    const effectsList = document.createElement('div');
    effectsList.id = 'effects-list';
    effectsList.style.cssText =
      'display: flex; flex-direction: column; gap: 2px; flex: 1; overflow-y: auto; padding: 2px;';
    leftPanel.appendChild(effectsList);

    const rightPanel = document.createElement('div');
    rightPanel.style.cssText = `
      flex: 1; background: #2a2a2a; border: 1px solid #444;
      border-radius: 6px; padding: 12px; display: flex; flex-direction: column;
    `;
    rightPanel.id = 'effect-editor-panel';

    container.appendChild(leftPanel);
    container.appendChild(rightPanel);
    host.appendChild(container);

    this.renderEffectsList(effectsList);
    this.renderEffectEditor(rightPanel, current);

    if (!document.getElementById('complex-global-controls')) {
      this.renderGlobalControls(host, current);
    }
  }

  private renderGlobalControls(host: HTMLDivElement, current: TtfTextSceneObject): void {
    const globalControls = document.createElement('div');
    globalControls.id = 'complex-global-controls';
    globalControls.style.marginTop = '16px';
    globalControls.style.padding = '12px';
    globalControls.style.backgroundColor = '#2a2a2a';
    globalControls.style.borderRadius = '6px';

    const globalTitle = document.createElement('h4');
    globalTitle.textContent = 'Global Settings';
    globalTitle.style.color = '#ffffff';
    globalTitle.style.margin = '0 0 8px 0';
    globalControls.appendChild(globalTitle);

    const globalForm = document.createElement('div');
    globalForm.style.display = 'grid';
    globalForm.style.gridTemplateColumns = '140px 1fr';
    globalForm.style.gap = '8px';
    globalForm.style.alignItems = 'center';

    const addGlobalRow = (label: string, input: HTMLElement) => {
      const l = document.createElement('label');
      l.textContent = label;
      l.style.color = '#ffffff';
      globalForm.appendChild(l);
      globalForm.appendChild(input);
    };

    const letterSpacingInput = this.createNumberInput(-100, 100, 0.1);
    letterSpacingInput.id = 'complex-letterSpacing';
    addGlobalRow('Letter Spacing', letterSpacingInput);

    const lineSpacingInput = this.createNumberInput(-100, 100, 0.1);
    lineSpacingInput.id = 'complex-lineSpacing';
    addGlobalRow('Line Spacing', lineSpacingInput);

    const scaleXInput = this.createNumberInput(0, 10, 0.01);
    scaleXInput.id = 'complex-scaleX';
    addGlobalRow('Scale X', scaleXInput);

    const scaleYInput = this.createNumberInput(0, 10, 0.01);
    scaleYInput.id = 'complex-scaleY';
    addGlobalRow('Scale Y', scaleYInput);

    const autoscaleEnabledInput = this.createCheckbox();
    autoscaleEnabledInput.id = 'complex-autoscale-enabled';
    addGlobalRow('Autoscale Enabled', autoscaleEnabledInput);

    const dimensionSourceSelect = this.createDimensionSourceSelect();
    dimensionSourceSelect.id = 'complex-dimension-source';
    addGlobalRow('Dimension Source', dimensionSourceSelect);

    const dimensionScaleSelect = this.createDimensionScaleSelect();
    dimensionScaleSelect.id = 'complex-dimension-scale';
    addGlobalRow('Dimension Scale', dimensionScaleSelect);

    const paddingTopInput = this.createNumberInput(0, 100, 1);
    paddingTopInput.id = 'complex-padding-top';
    addGlobalRow('Padding Top', paddingTopInput);

    const paddingRightInput = this.createNumberInput(0, 100, 1);
    paddingRightInput.id = 'complex-padding-right';
    addGlobalRow('Padding Right', paddingRightInput);

    const paddingBottomInput = this.createNumberInput(0, 100, 1);
    paddingBottomInput.id = 'complex-padding-bottom';
    addGlobalRow('Padding Bottom', paddingBottomInput);

    const paddingLeftInput = this.createNumberInput(0, 100, 1);
    paddingLeftInput.id = 'complex-padding-left';
    addGlobalRow('Padding Left', paddingLeftInput);

    globalControls.appendChild(globalForm);
    host.appendChild(globalControls);

    globalForm.addEventListener('input', () => this.applyComplexEffects(current));

    if (this.currentEffects.length === 0) {
      this.loadCurrentEffects(current);
    }
    this.populateComplexForm(current);

    // Apply restored values to objects
    this.applyComplexEffects(current);
  }

  private loadCurrentEffects(current: TtfTextSceneObject): void {
    const rc = current.getRenderComponent()?.getConfig();

    if (rc?.complexEffects) {
      this.currentEffects = this.convertComplexEffectsToLocal(rc.complexEffects);
    } else {
      this.currentEffects = [];
    }
    this.selectedEffectIndex = -1;
  }

  private loadCurrentEffectsFromSession(current: TtfTextSceneObject): void {
    // Priority: session.complex -> session.legacy (raw fallback) -> object config
    const entry = this.getSessionEntry(current);
    if (entry?.complex?.complexEffects?.effects) {
      this.currentEffects = JSON.parse(JSON.stringify(entry.complex.complexEffects.effects));
      this.selectedEffectIndex = -1;
    } else if (entry?.legacy?.complexEffects) {
      this.currentEffects = JSON.parse(
        JSON.stringify((entry.legacy as any).complexEffects.effects || [])
      );
      this.selectedEffectIndex = -1;
    } else {
      this.loadCurrentEffects(current);
    }
  }

  private convertComplexEffectsToLocal(complexEffects: any): EffectConfig[] {
    return (complexEffects.effects || []).map((effect: any, index: number) => ({
      id: effect.id || `effect_${index}`,
      type: effect.type,
      enabled: effect.enabled !== false,
      order: effect.order || index,
      color: effect.color ? this.color4ToHex6(effect.color) : undefined,
      config: effect.config,
    }));
  }

  private renderEffectsList(container: HTMLElement): void {
    container.innerHTML = '';

    if (this.currentEffects.length === 0) {
      const emptyState = document.createElement('div');
      emptyState.style.cssText =
        'color: #888; text-align: center; padding: 20px; font-style: italic; font-size: 12px;';
      emptyState.textContent = 'No effects added yet. Click buttons above to add effects.';
      container.appendChild(emptyState);
      return;
    }

    this.currentEffects
      .sort((a, b) => a.order - b.order)
      .forEach((effect, index) => {
        const item = document.createElement('div');
        const isSelected = this.selectedEffectIndex === index;

        item.style.cssText = `
          padding: 10px; background: ${isSelected ? 'linear-gradient(135deg, #4a4a4a, #555)' : '#333'};
          border: 2px solid ${isSelected ? '#6a9bd1' : '#444'}; border-radius: 6px;
          cursor: pointer; color: white; display: flex; align-items: center;
          gap: 8px; transition: all 0.2s; position: relative;
        `;

        const effectInfo = document.createElement('div');
        effectInfo.style.cssText = 'flex: 1; display: flex; align-items: center; gap: 8px;';

        const typeName = document.createElement('span');
        typeName.textContent = effect.type.charAt(0).toUpperCase() + effect.type.slice(1);
        typeName.style.cssText = 'font-weight: bold; font-size: 12px;';
        effectInfo.appendChild(typeName);

        const statusIndicator = document.createElement('div');
        statusIndicator.style.cssText = `
          width: 8px; height: 8px; border-radius: 50%;
          background: ${effect.enabled ? '#4CAF50' : '#f44336'}; flex-shrink: 0;
        `;

        const buttonsContainer = document.createElement('div');
        buttonsContainer.style.cssText = `display: flex; gap: 2px; opacity: ${isSelected ? '1' : '0.7'};`;

        const createActionBtn = (text: string, color: string, onClick: () => void) => {
          const btn = document.createElement('button');
          btn.textContent = text;
          btn.style.cssText = `
            background: ${color}; color: white; border: none; border-radius: 3px;
            width: 22px; height: 22px; cursor: pointer; font-size: 11px; font-weight: bold;
          `;
          btn.onclick = (e) => {
            e.stopPropagation();
            onClick();
          };
          return btn;
        };

        buttonsContainer.appendChild(
          createActionBtn('\u2191', '#4a90e2', () => this.moveEffectUp(index))
        );
        buttonsContainer.appendChild(
          createActionBtn('\u2193', '#4a90e2', () => this.moveEffectDown(index))
        );
        buttonsContainer.appendChild(
          createActionBtn('\u00d7', '#e74c3c', () => this.removeEffect(index))
        );

        item.appendChild(effectInfo);
        item.appendChild(statusIndicator);
        item.appendChild(buttonsContainer);

        item.onclick = () => {
          this.selectedEffectIndex = index;
          this.renderEffectsList(container);
          const rightPanel = document.getElementById('effect-editor-panel');
          if (rightPanel) {
            const cur = this.getPrimarySelected();
            if (cur) this.renderEffectEditor(rightPanel, cur);
          }
        };

        container.appendChild(item);
      });
  }

  private renderEffectEditor(container: HTMLElement, current: TtfTextSceneObject): void {
    container.innerHTML = '';

    if (this.selectedEffectIndex < 0 || this.selectedEffectIndex >= this.currentEffects.length) {
      const emptyState = document.createElement('div');
      emptyState.style.cssText =
        'color: #888; text-align: center; padding: 40px 20px; font-style: italic; font-size: 14px;';
      emptyState.textContent = 'Select an effect from the list to edit its properties';
      container.appendChild(emptyState);
      return;
    }

    const effect = this.currentEffects[this.selectedEffectIndex];

    const header = document.createElement('div');
    header.style.cssText =
      'display: flex; align-items: center; gap: 12px; margin-bottom: 20px; padding-bottom: 12px; border-bottom: 1px solid #444;';

    const title = document.createElement('h3');
    title.textContent = `${effect.type.charAt(0).toUpperCase() + effect.type.slice(1)} Effect`;
    title.style.cssText = 'color: #ffffff; margin: 0; font-size: 16px; font-weight: bold; flex: 1;';

    const toggleBtn = document.createElement('button');
    toggleBtn.textContent = effect.enabled ? 'Disable' : 'Enable';
    toggleBtn.style.cssText = `
      background: ${effect.enabled ? '#e74c3c' : '#27ae60'};
      color: white; border: none; padding: 6px 12px; border-radius: 4px;
      cursor: pointer; font-size: 12px; font-weight: bold;
    `;
    toggleBtn.onclick = () => {
      effect.enabled = !effect.enabled;
      this.renderEffectEditor(container, current);
      this.renderEffectsList(document.getElementById('effects-list')!);
      this.applyComplexEffects(current);
    };

    header.appendChild(title);
    header.appendChild(toggleBtn);
    container.appendChild(header);

    const formContainer = document.createElement('div');
    formContainer.style.cssText = 'flex: 1; overflow-y: auto; padding: 4px;';

    const formGrid = document.createElement('div');
    formGrid.style.cssText =
      'display: grid; grid-template-columns: 120px 1fr; gap: 12px; align-items: center;';

    const addRow = (label: string, input: HTMLElement) => {
      const l = document.createElement('label');
      l.textContent = label;
      l.style.cssText = 'color: #ffffff; font-size: 12px; font-weight: bold;';
      formGrid.appendChild(l);
      formGrid.appendChild(input);
    };

    const enabledInput = this.createCheckbox();
    enabledInput.id = `effect-${this.selectedEffectIndex}-enabled`;
    enabledInput.checked = effect.enabled !== false;
    addRow('Enabled', enabledInput);

    const orderInput = this.createNumberInput(0, 100, 1);
    orderInput.id = `effect-${this.selectedEffectIndex}-order`;
    orderInput.value = String(effect.order);
    addRow('Order', orderInput);

    this.renderEffectTypeFields(formGrid, effect, this.selectedEffectIndex);

    formContainer.appendChild(formGrid);
    container.appendChild(formContainer);

    formGrid.addEventListener('input', () => {
      this.updateEffectFromForm(this.selectedEffectIndex);
      this.applyComplexEffects(current);
    });
  }

  private renderEffectTypeFields(
    container: HTMLElement,
    effect: EffectConfig,
    index: number
  ): void {
    const addRow = (label: string, input: HTMLElement) => {
      const l = document.createElement('label');
      l.textContent = label;
      l.style.color = '#ffffff';
      container.appendChild(l);
      container.appendChild(input);
    };

    switch (effect.type) {
      case EffectType.Fill: {
        const colorInput = this.createColorPair(`effect-${index}-color`, '#ffffff');
        addRow('Color', colorInput);
        if (effect.color) {
          const textInput = colorInput.querySelector('input[type="text"]') as HTMLInputElement;
          if (textInput) {
            textInput.value = effect.color;
            const picker = colorInput.querySelector('input[type="color"]') as HTMLInputElement;
            if (picker) {
              const hex = this.colorToHex(effect.color);
              if (hex) picker.value = hex;
            }
          }
        }
        break;
      }

      case EffectType.Gradient: {
        const gradConfig = (effect.config as GradientFillConfig) || {};

        const angleInput = this.createNumberInput(0, 360, 1);
        angleInput.id = `effect-${index}-angle`;
        angleInput.value = String(gradConfig.angle || 0);
        addRow('Angle', angleInput);

        const offsetInput = this.createNumberInput(-1, 1, 0.01);
        offsetInput.id = `effect-${index}-offset`;
        offsetInput.value = String(gradConfig.offset || 0);
        addRow('Offset', offsetInput);

        const stops = gradConfig.stops || [
          { offset: 0, color: '#ffffff' },
          { offset: 1, color: '#000000' },
        ];

        const stop0Color = this.createColorPair(`effect-${index}-stop0-color`, '#ff0000');
        addRow('Stop 0 Color', stop0Color);
        if (stops[0]?.color) {
          const s0t = stop0Color.querySelector('input[type="text"]') as HTMLInputElement;
          if (s0t) {
            s0t.value = stops[0].color;
            const s0p = stop0Color.querySelector('input[type="color"]') as HTMLInputElement;
            if (s0p) {
              const h = this.colorToHex(stops[0].color);
              if (h) s0p.value = h;
            }
          }
        }

        const stop0Offset = this.createNumberInput(0, 1, 0.01);
        stop0Offset.id = `effect-${index}-stop0-offset`;
        stop0Offset.value = String(stops[0]?.offset || 0);
        addRow('Stop 0 Offset', stop0Offset);

        const stop1Color = this.createColorPair(`effect-${index}-stop1-color`, '#0000ff');
        addRow('Stop 1 Color', stop1Color);
        if (stops[1]?.color) {
          const s1t = stop1Color.querySelector('input[type="text"]') as HTMLInputElement;
          if (s1t) {
            s1t.value = stops[1].color;
            const s1p = stop1Color.querySelector('input[type="color"]') as HTMLInputElement;
            if (s1p) {
              const h = this.colorToHex(stops[1].color);
              if (h) s1p.value = h;
            }
          }
        }

        const stop1Offset = this.createNumberInput(0, 1, 0.01);
        stop1Offset.id = `effect-${index}-stop1-offset`;
        stop1Offset.value = String(stops[1]?.offset || 1);
        addRow('Stop 1 Offset', stop1Offset);
        break;
      }

      case EffectType.OuterGlow:
      case EffectType.InnerGlow: {
        const glowConfig = (effect.config as GlowStrokeConfig) || {};

        const glowColor = this.createColorPair(`effect-${index}-color`, '#ffffff');
        addRow('Color', glowColor);
        if (glowConfig.color) {
          const gt = glowColor.querySelector('input[type="text"]') as HTMLInputElement;
          if (gt) {
            const colorStr =
              typeof glowConfig.color === 'string'
                ? glowConfig.color
                : this.color4ToHex6(glowConfig.color as any);
            gt.value = colorStr;
            const gp = glowColor.querySelector('input[type="color"]') as HTMLInputElement;
            if (gp) {
              const h = this.colorToHex(colorStr);
              if (h) gp.value = h;
            }
          }
        }

        const widthInput = this.createNumberInput(0, 100, 1);
        widthInput.id = `effect-${index}-width`;
        widthInput.value = String(glowConfig.width || 0);
        addRow('Width', widthInput);

        const softnessInput = this.createNumberInput(0, 100, 1);
        softnessInput.id = `effect-${index}-softness`;
        softnessInput.value = String(glowConfig.softness || 0);
        addRow('Softness', softnessInput);

        const opacityInput = this.createNumberInput(0, 1, 0.05);
        opacityInput.id = `effect-${index}-opacity`;
        opacityInput.value = String(glowConfig.opacity || 1);
        addRow('Opacity', opacityInput);
        break;
      }

      case EffectType.Shadow: {
        const shadowConfig = (effect.config as ShadowConfig) || {};

        const shadowColor = this.createColorPair(`effect-${index}-color`, 'rgba(0,0,0,0.5)');
        addRow('Color', shadowColor);
        if (shadowConfig.color) {
          const st = shadowColor.querySelector('input[type="text"]') as HTMLInputElement;
          if (st) {
            const colorStr =
              typeof shadowConfig.color === 'string'
                ? shadowConfig.color
                : this.color4ToHex6(shadowConfig.color as any);
            st.value = colorStr;
            const sp = shadowColor.querySelector('input[type="color"]') as HTMLInputElement;
            if (sp) {
              const h = this.colorToHex(colorStr);
              if (h) sp.value = h;
            }
          }
        }

        const offsetXInput = this.createNumberInput(-100, 100, 1);
        offsetXInput.id = `effect-${index}-offsetX`;
        offsetXInput.value = String(shadowConfig.offsetX || 0);
        addRow('Offset X', offsetXInput);

        const offsetYInput = this.createNumberInput(-100, 100, 1);
        offsetYInput.id = `effect-${index}-offsetY`;
        offsetYInput.value = String(shadowConfig.offsetY || 0);
        addRow('Offset Y', offsetYInput);

        const shadowSoftness = this.createNumberInput(0, 100, 1);
        shadowSoftness.id = `effect-${index}-softness`;
        shadowSoftness.value = String(shadowConfig.softness || 0);
        addRow('Softness', shadowSoftness);

        const shadowOpacity = this.createNumberInput(0, 1, 0.05);
        shadowOpacity.id = `effect-${index}-opacity`;
        shadowOpacity.value = String(shadowConfig.opacity || 1);
        addRow('Opacity', shadowOpacity);
        break;
      }
    }
  }

  private addNewEffect(
    host: HTMLDivElement,
    current: TtfTextSceneObject,
    effectType: EffectType
  ): void {
    const newEffect: EffectConfig = {
      id: `effect_${Date.now()}`,
      type: effectType,
      enabled: true,
      order: this.currentEffects.length,
    };

    switch (effectType) {
      case EffectType.Fill:
        newEffect.color = '#ffffff';
        break;
      case EffectType.Gradient:
        newEffect.config = {
          enabled: true,
          type: 'linear',
          angle: 0,
          offset: 0,
          stops: [
            { offset: 0, color: '#ffffff' },
            { offset: 1, color: '#000000' },
          ],
        };
        break;
      case EffectType.OuterGlow:
      case EffectType.InnerGlow:
        newEffect.config = { enabled: true, color: '#ffffff', width: 5, softness: 3, opacity: 1 };
        break;
      case EffectType.Shadow:
        newEffect.config = {
          enabled: true,
          color: 'rgba(0,0,0,0.5)',
          offsetX: 2,
          offsetY: 2,
          softness: 2,
          opacity: 0.8,
        };
        break;
    }

    this.currentEffects.push(newEffect);
    this.selectedEffectIndex = this.currentEffects.length - 1;
    this.renderEffectsEditor(host, current);
  }

  private removeEffect(index: number): void {
    this.currentEffects.splice(index, 1);
    if (this.selectedEffectIndex >= this.currentEffects.length) {
      this.selectedEffectIndex = this.currentEffects.length - 1;
    }
    const tabContent = document.querySelector('#ttf-tab-content') as HTMLDivElement;
    const current = this.getPrimarySelected();
    if (tabContent && current) this.renderEffectsEditor(tabContent, current);
  }

  private moveEffectUp(index: number): void {
    if (index <= 0) return;
    const temp = this.currentEffects[index];
    this.currentEffects[index] = this.currentEffects[index - 1];
    this.currentEffects[index - 1] = temp;
    this.currentEffects[index].order = index;
    this.currentEffects[index - 1].order = index - 1;
    if (this.selectedEffectIndex === index) this.selectedEffectIndex = index - 1;
    else if (this.selectedEffectIndex === index - 1) this.selectedEffectIndex = index;
    const tabContent = document.querySelector('#ttf-tab-content') as HTMLDivElement;
    const current = this.getPrimarySelected();
    if (tabContent && current) {
      this.renderEffectsEditor(tabContent, current);
      this.applyComplexEffects(current);
    }
  }

  private moveEffectDown(index: number): void {
    if (index >= this.currentEffects.length - 1) return;
    const temp = this.currentEffects[index];
    this.currentEffects[index] = this.currentEffects[index + 1];
    this.currentEffects[index + 1] = temp;
    this.currentEffects[index].order = index;
    this.currentEffects[index + 1].order = index + 1;
    if (this.selectedEffectIndex === index) this.selectedEffectIndex = index + 1;
    else if (this.selectedEffectIndex === index + 1) this.selectedEffectIndex = index;
    const tabContent = document.querySelector('#ttf-tab-content') as HTMLDivElement;
    const current = this.getPrimarySelected();
    if (tabContent && current) {
      this.renderEffectsEditor(tabContent, current);
      this.applyComplexEffects(current);
    }
  }

  private updateEffectFromForm(index: number): void {
    if (index < 0 || index >= this.currentEffects.length) return;
    const effect = this.currentEffects[index];
    const getValue = (id: string) => (document.querySelector(`#${id}`) as HTMLInputElement)?.value;
    const getChecked = (id: string) =>
      (document.querySelector(`#${id}`) as HTMLInputElement)?.checked;

    effect.enabled = getChecked(`effect-${index}-enabled`) ?? true;
    effect.order = parseInt(getValue(`effect-${index}-order`) || '0', 10);

    switch (effect.type) {
      case EffectType.Fill:
        effect.color = getValue(`effect-${index}-color`) || '#ffffff';
        break;
      case EffectType.Gradient: {
        if (!effect.config)
          effect.config = { enabled: true, type: 'linear', angle: 0, offset: 0, stops: [] };
        const gc = effect.config as GradientFillConfig;
        gc.enabled = true;
        gc.type = 'linear';
        gc.angle = parseFloat(getValue(`effect-${index}-angle`) || '0');
        gc.offset = parseFloat(getValue(`effect-${index}-offset`) || '0');
        gc.stops = [];
        const s0c = getValue(`effect-${index}-stop0-color`);
        const s0o = getValue(`effect-${index}-stop0-offset`);
        if (s0c) gc.stops.push({ offset: parseFloat(s0o || '0'), color: s0c });
        const s1c = getValue(`effect-${index}-stop1-color`);
        const s1o = getValue(`effect-${index}-stop1-offset`);
        if (s1c) gc.stops.push({ offset: parseFloat(s1o || '1'), color: s1c });
        break;
      }
      case EffectType.OuterGlow:
      case EffectType.InnerGlow: {
        if (!effect.config) effect.config = {};
        const gc = effect.config as GlowStrokeConfig;
        gc.enabled = true;
        const c = getValue(`effect-${index}-color`);
        if (c) gc.color = c;
        gc.width = parseFloat(getValue(`effect-${index}-width`) || '0');
        gc.softness = parseFloat(getValue(`effect-${index}-softness`) || '0');
        gc.opacity = parseFloat(getValue(`effect-${index}-opacity`) || '1');
        break;
      }
      case EffectType.Shadow: {
        if (!effect.config) effect.config = {};
        const sc = effect.config as ShadowConfig;
        sc.enabled = true;
        const c = getValue(`effect-${index}-color`);
        if (c) sc.color = c;
        sc.offsetX = parseFloat(getValue(`effect-${index}-offsetX`) || '0');
        sc.offsetY = parseFloat(getValue(`effect-${index}-offsetY`) || '0');
        sc.softness = parseFloat(getValue(`effect-${index}-softness`) || '0');
        sc.opacity = parseFloat(getValue(`effect-${index}-opacity`) || '1');
        break;
      }
    }
  }

  private applyComplexEffects(current: TtfTextSceneObject): void {
    const complexConfig: ComplexRenderConfig = {
      effects: this.convertLocalEffectsToRender(),
      letterSpacing: this.getComplexGlobalValue('complex-letterSpacing'),
      lineSpacing: this.getComplexGlobalValue('complex-lineSpacing'),
      canvasScaleX: this.getComplexGlobalValue('complex-scaleX'),
      canvasScaleY: this.getComplexGlobalValue('complex-scaleY'),
    };

    const renderConfig: any = { complexEffects: complexConfig };

    const autoscaleEnabled = (
      document.querySelector('#complex-autoscale-enabled') as HTMLInputElement
    )?.checked;
    if (autoscaleEnabled) {
      const dsSelect = document.querySelector<HTMLSelectElement>('#complex-dimension-source');
      const dscSelect = document.querySelector<HTMLSelectElement>('#complex-dimension-scale');
      if (dsSelect?.value) renderConfig.dimensionSource = dsSelect.value;
      if (dscSelect?.value) renderConfig.dimensionSourceScale = dscSelect.value;
    }

    const pT = this.getComplexGlobalValue('complex-padding-top');
    if (pT !== undefined) renderConfig.paddingTop = pT;
    const pR = this.getComplexGlobalValue('complex-padding-right');
    if (pR !== undefined) renderConfig.paddingRight = pR;
    const pB = this.getComplexGlobalValue('complex-padding-bottom');
    if (pB !== undefined) renderConfig.paddingBottom = pB;
    const pL = this.getComplexGlobalValue('complex-padding-left');
    if (pL !== undefined) renderConfig.paddingLeft = pL;

    const selected = this.getSelectedObjects();
    const targets = selected.length > 0 ? selected : [current];
    for (const obj of targets) {
      const renderComponent = new TtfRenderComponent(renderConfig);
      obj.setRenderComponent(renderComponent);
      this.snapshotComplexTab(obj);
    }
  }

  private convertColorToString(color: string | Color4 | undefined): string | undefined {
    if (!color) return undefined;
    if (typeof color === 'string') return color;
    return this.color4ToHex6(color);
  }

  private convertLocalEffectsToRender(): RenderEffect[] {
    return this.currentEffects.map((effect) => {
      const baseEffect = { id: effect.id, enabled: effect.enabled, order: effect.order };

      switch (effect.type) {
        case EffectType.Fill:
          return {
            ...baseEffect,
            type: EffectType.Fill,
            color: this.parseColor(effect.color || '#ffffff'),
          } as RenderEffect;
        case EffectType.Gradient: {
          const gc = effect.config as GradientFillConfig;
          return {
            ...baseEffect,
            type: EffectType.Gradient,
            config: {
              ...gc,
              stops: (gc.stops || []).map((s) => ({
                offset: s.offset ?? 0,
                color: this.parseColor(s.color || '#ffffff'),
              })),
            },
          } as RenderEffect;
        }
        case EffectType.OuterGlow: {
          const gc = effect.config as GlowStrokeConfig;
          return {
            ...baseEffect,
            type: EffectType.OuterGlow,
            config: {
              ...gc,
              color: gc.color
                ? this.parseColor(this.convertColorToString(gc.color) || '#ffffff')
                : undefined,
            },
          } as RenderEffect;
        }
        case EffectType.InnerGlow: {
          const gc = effect.config as GlowStrokeConfig;
          return {
            ...baseEffect,
            type: EffectType.InnerGlow,
            config: {
              ...gc,
              color: gc.color
                ? this.parseColor(this.convertColorToString(gc.color) || '#ffffff')
                : undefined,
            },
          } as RenderEffect;
        }
        case EffectType.Shadow: {
          const sc = effect.config as ShadowConfig;
          return {
            ...baseEffect,
            type: EffectType.Shadow,
            config: {
              ...sc,
              color: sc.color
                ? this.parseColor(this.convertColorToString(sc.color) || 'rgba(0,0,0,0.5)')
                : undefined,
            },
          } as RenderEffect;
        }
        default:
          throw new Error(`Unknown effect type: ${effect.type}`);
      }
    });
  }

  private getComplexGlobalValue(id: string): number | undefined {
    const el = document.querySelector(`#${id}`) as HTMLInputElement;
    if (!el || !el.value) return undefined;
    return parseFloat(el.value);
  }

  private populateComplexForm(current: TtfTextSceneObject): void {
    // Priority: session.complex -> session.legacy (raw fallback) -> object config
    const entry = this.getSessionEntry(current);
    if (entry?.complex) {
      this.applyComplexGlobalsFromSource(entry.complex);
      return;
    }
    if (entry?.legacy) {
      this.applyComplexGlobalsFromSource(entry.legacy);
      return;
    }

    // Fallback: read from object
    const rc = current.getRenderComponent()?.getConfig();
    const complexConfig = rc?.complexEffects;
    if (complexConfig) {
      this.setComplexValue('complex-letterSpacing', complexConfig.letterSpacing);
      this.setComplexValue('complex-lineSpacing', complexConfig.lineSpacing);
      this.setComplexValue('complex-scaleX', complexConfig.canvasScaleX);
      this.setComplexValue('complex-scaleY', complexConfig.canvasScaleY);
    }
    if (rc) {
      const hasAutoscale = rc.dimensionSource && rc.dimensionSourceScale;
      const autoscaleCheckbox = document.querySelector<HTMLInputElement>(
        '#complex-autoscale-enabled'
      );
      if (autoscaleCheckbox) autoscaleCheckbox.checked = !!hasAutoscale;
      if (rc.dimensionSource) {
        const dsSelect = document.querySelector<HTMLSelectElement>('#complex-dimension-source');
        if (dsSelect) dsSelect.value = rc.dimensionSource as string;
      }
      if (rc.dimensionSourceScale) {
        const dscSelect = document.querySelector<HTMLSelectElement>('#complex-dimension-scale');
        if (dscSelect) dscSelect.value = rc.dimensionSourceScale as string;
      }
      if (rc.paddingTop !== undefined) this.setComplexValue('complex-padding-top', rc.paddingTop);
      if (rc.paddingRight !== undefined)
        this.setComplexValue('complex-padding-right', rc.paddingRight);
      if (rc.paddingBottom !== undefined)
        this.setComplexValue('complex-padding-bottom', rc.paddingBottom);
      if (rc.paddingLeft !== undefined)
        this.setComplexValue('complex-padding-left', rc.paddingLeft);
    }
  }

  private applyComplexGlobalsFromSource(s: any): void {
    const ce = s.complexEffects || {};
    this.setComplexValue('complex-letterSpacing', ce.letterSpacing ?? s.letterSpacing);
    this.setComplexValue('complex-lineSpacing', ce.lineSpacing ?? s.lineSpacing);
    this.setComplexValue('complex-scaleX', ce.canvasScaleX ?? s.canvasScaleX);
    this.setComplexValue('complex-scaleY', ce.canvasScaleY ?? s.canvasScaleY);

    const hasAutoscale = s.dimensionSource && s.dimensionSourceScale;
    const autoscaleCheckbox = document.querySelector<HTMLInputElement>(
      '#complex-autoscale-enabled'
    );
    if (autoscaleCheckbox) autoscaleCheckbox.checked = !!hasAutoscale;
    if (s.dimensionSource) {
      const dsSelect = document.querySelector<HTMLSelectElement>('#complex-dimension-source');
      if (dsSelect) dsSelect.value = s.dimensionSource;
    }
    if (s.dimensionSourceScale) {
      const dscSelect = document.querySelector<HTMLSelectElement>('#complex-dimension-scale');
      if (dscSelect) dscSelect.value = s.dimensionSourceScale;
    }
    if (s.paddingTop !== undefined) this.setComplexValue('complex-padding-top', s.paddingTop);
    if (s.paddingRight !== undefined) this.setComplexValue('complex-padding-right', s.paddingRight);
    if (s.paddingBottom !== undefined)
      this.setComplexValue('complex-padding-bottom', s.paddingBottom);
    if (s.paddingLeft !== undefined) this.setComplexValue('complex-padding-left', s.paddingLeft);
  }

  private setComplexValue(id: string, value: number | undefined): void {
    const el = document.querySelector(`#${id}`) as HTMLInputElement;
    if (el && value !== undefined) el.value = String(value);
  }

  private copyComplexJsonFromForm(host: HTMLDivElement): void {
    const selected = this.getSelectedObjects();
    if (selected.length === 0) return;
    const complexConfig: ComplexRenderConfigLocal = {
      effects: this.currentEffects,
      letterSpacing: this.getComplexGlobalValue('complex-letterSpacing'),
      lineSpacing: this.getComplexGlobalValue('complex-lineSpacing'),
      canvasScaleX: this.getComplexGlobalValue('complex-scaleX'),
      canvasScaleY: this.getComplexGlobalValue('complex-scaleY'),
    };
    const renderConfig: any = { complexEffects: complexConfig };
    const autoscaleEnabled = (
      document.querySelector('#complex-autoscale-enabled') as HTMLInputElement
    )?.checked;
    if (autoscaleEnabled) {
      const dsSelect = document.querySelector<HTMLSelectElement>('#complex-dimension-source');
      const dscSelect = document.querySelector<HTMLSelectElement>('#complex-dimension-scale');
      if (dsSelect?.value) renderConfig.dimensionSource = dsSelect.value;
      if (dscSelect?.value) renderConfig.dimensionSourceScale = dscSelect.value;
    }
    const scenesMap = new Map<string, { id: string; render: any }[]>();
    for (const obj of selected) {
      const sceneId = this.normalizeSceneUrlToSceneId(obj.sceneUrl) || 'scene_id_here';
      if (!scenesMap.has(sceneId)) scenesMap.set(sceneId, []);
      scenesMap.get(sceneId)!.push({ id: obj.id || 'text_id_here', render: renderConfig });
    }
    const payload = {
      scenes: Array.from(scenesMap.entries()).map(([id, items]) => ({ id, items })),
    };
    const json = JSON.stringify(payload, null, 2);
    this.copyToClipboard(json, host);
  }

  private createNumberInput(min?: number, max?: number, step?: number): HTMLInputElement {
    const input = document.createElement('input');
    input.type = 'number';
    if (min !== undefined) input.min = String(min);
    if (max !== undefined) input.max = String(max);
    if (step !== undefined) input.step = String(step);
    input.style.cssText =
      'width: 100%; background: #3a3a3a; color: #ffffff; border: 1px solid #555; height: 32px; padding: 4px 8px; border-radius: 6px;';
    return input;
  }

  private createCheckbox(): HTMLInputElement {
    const input = document.createElement('input');
    input.type = 'checkbox';
    return input;
  }

  private createColorPair(baseId: string, placeholder = '#RRGGBB'): HTMLDivElement {
    const wrap = document.createElement('div');
    wrap.style.display = 'grid';
    wrap.style.gridTemplateColumns = '90px 1fr';
    wrap.style.gap = '6px';

    const picker = document.createElement('input');
    picker.type = 'color';
    picker.id = `${baseId}-picker`;
    picker.style.cssText =
      'width: 90px; height: 32px; border: 1px solid #555; background: #3a3a3a; border-radius: 6px;';

    const text = document.createElement('input');
    text.type = 'text';
    text.id = baseId;
    text.placeholder = placeholder;
    text.style.cssText =
      'width: 100%; background: #3a3a3a; color: #ffffff; border: 1px solid #555; height: 32px; padding: 4px 8px; border-radius: 6px;';

    picker.addEventListener('input', () => {
      text.value = picker.value;
    });
    text.addEventListener('input', () => {
      const hex = this.colorToHex(text.value);
      if (hex) picker.value = hex;
    });

    wrap.appendChild(picker);
    wrap.appendChild(text);
    return wrap;
  }

  private createDimensionSourceSelect(): HTMLSelectElement {
    const select = document.createElement('select');
    select.style.cssText =
      'width: 100%; background: #3a3a3a; color: #ffffff; border: 1px solid #555; height: 32px; padding: 4px 8px; border-radius: 6px;';
    [
      { value: '', label: 'None' },
      { value: 'Frame', label: 'Frame' },
      { value: 'ProportionalSize', label: 'Proportional Size' },
      { value: 'Size', label: 'Size' },
    ].forEach((opt) => {
      const o = document.createElement('option');
      o.value = opt.value;
      o.textContent = opt.label;
      select.appendChild(o);
    });
    return select;
  }

  private createDimensionScaleSelect(): HTMLSelectElement {
    const select = document.createElement('select');
    select.style.cssText =
      'width: 100%; background: #3a3a3a; color: #ffffff; border: 1px solid #555; height: 32px; padding: 4px 8px; border-radius: 6px;';
    [
      { value: '', label: 'None' },
      { value: 'DownOnly', label: 'Down Only' },
      { value: 'UpOnly', label: 'Up Only' },
      { value: 'Both', label: 'Both' },
    ].forEach((opt) => {
      const o = document.createElement('option');
      o.value = opt.value;
      o.textContent = opt.label;
      select.appendChild(o);
    });
    return select;
  }

  private populateFormFromObject(host: HTMLDivElement, obj: TtfTextSceneObject): void {
    const rc = obj.getRenderComponent()?.getConfig();
    const setVal = (id: string, value: string | number | undefined) => {
      const el = host.querySelector<HTMLInputElement>(`#${id}`);
      if (!el || value === undefined || value === null) return;
      el.value = String(value);
    };
    const setChecked = (id: string, value: boolean | undefined) => {
      const el = host.querySelector<HTMLInputElement>(`#${id}`);
      if (!el || value === undefined || value === null) return;
      el.checked = !!value;
    };

    if (rc) {
      if (rc.fillColor) setVal('rc-fillColor', this.color4ToHex6(rc.fillColor));
      if (rc.fillGradient) {
        setChecked('rc-grad-enabled', rc.fillGradient.enabled !== false);
        if (rc.fillGradient.angle !== undefined) setVal('rc-grad-angle', rc.fillGradient.angle);
        if ((rc.fillGradient as any).offset !== undefined)
          setVal('rc-grad-offset', (rc.fillGradient as any).offset);
        const stops = rc.fillGradient.stops || [];
        if (stops[0]) {
          setVal('rc-grad-stop0-color', this.color4ToHex6(stops[0].color));
          if (stops[0].offset !== undefined) setVal('rc-grad-stop0-offset', stops[0].offset);
        }
        if (stops[1]) {
          setVal('rc-grad-stop1-color', this.color4ToHex6(stops[1].color));
          if (stops[1].offset !== undefined) setVal('rc-grad-stop1-offset', stops[1].offset);
        }
      }
      if (rc.outerGlow) {
        setChecked('rc-og-enabled', rc.outerGlow.enabled !== false);
        if (rc.outerGlow.color) setVal('rc-og-color', this.color4ToHex6(rc.outerGlow.color));
        if (rc.outerGlow.width !== undefined) setVal('rc-og-width', rc.outerGlow.width);
        if (rc.outerGlow.softness !== undefined) setVal('rc-og-softness', rc.outerGlow.softness);
        if (rc.outerGlow.opacity !== undefined) setVal('rc-og-opacity', rc.outerGlow.opacity);
      }
      if (rc.innerGlow) {
        setChecked('rc-ig-enabled', rc.innerGlow.enabled !== false);
        if (rc.innerGlow.color) setVal('rc-ig-color', this.color4ToHex6(rc.innerGlow.color));
        if (rc.innerGlow.width !== undefined) setVal('rc-ig-width', rc.innerGlow.width);
        if (rc.innerGlow.softness !== undefined) setVal('rc-ig-softness', rc.innerGlow.softness);
        if (rc.innerGlow.opacity !== undefined) setVal('rc-ig-opacity', rc.innerGlow.opacity);
      }
      if (rc.shadow) {
        setChecked('rc-sh-enabled', rc.shadow.enabled !== false);
        if (rc.shadow.color) setVal('rc-sh-color', this.color4ToHex6(rc.shadow.color));
        if (rc.shadow.offsetX !== undefined) setVal('rc-sh-offsetX', rc.shadow.offsetX);
        if (rc.shadow.offsetY !== undefined) setVal('rc-sh-offsetY', rc.shadow.offsetY);
        if (rc.shadow.softness !== undefined) setVal('rc-sh-softness', rc.shadow.softness);
        if (rc.shadow.opacity !== undefined) setVal('rc-sh-opacity', rc.shadow.opacity);
      }
      if (rc.letterSpacing !== undefined) setVal('rc-letterSpacing', rc.letterSpacing);
      if (rc.lineSpacing !== undefined) setVal('rc-lineSpacing', rc.lineSpacing);
      if (rc.canvasScaleX !== undefined) setVal('rc-scaleX', rc.canvasScaleX);
      if (rc.canvasScaleY !== undefined) setVal('rc-scaleY', rc.canvasScaleY);
      const hasAutoscale = !!(rc.dimensionSource && rc.dimensionSourceScale);
      setChecked('rc-autoscale-enabled', hasAutoscale);
      if (rc.dimensionSource) {
        const ds = host.querySelector<HTMLSelectElement>('#rc-dimension-source');
        if (ds) ds.value = rc.dimensionSource as string;
      }
      if (rc.dimensionSourceScale) {
        const dsc = host.querySelector<HTMLSelectElement>('#rc-dimension-scale');
        if (dsc) dsc.value = rc.dimensionSourceScale as string;
      }
      if (rc.paddingTop !== undefined) setVal('rc-padding-top', rc.paddingTop);
      if (rc.paddingRight !== undefined) setVal('rc-padding-right', rc.paddingRight);
      if (rc.paddingBottom !== undefined) setVal('rc-padding-bottom', rc.paddingBottom);
      if (rc.paddingLeft !== undefined) setVal('rc-padding-left', rc.paddingLeft);
    } else {
      setVal('rc-letterSpacing', obj.textRenderParams.letterSpacing ?? 0);
      setVal('rc-lineSpacing', obj.lineSpacing ?? 0);
    }
  }

  private populateFormFromRenderConfig(host: HTMLDivElement, cfg: any): void {
    const setVal = (id: string, value: string | number | undefined) => {
      const el = host.querySelector<HTMLInputElement>(`#${id}`);
      if (!el || value === undefined || value === null) return;
      el.value = String(value);
      const picker = host.querySelector<HTMLInputElement>(`#${id}-picker`);
      if (picker) {
        const hex = this.colorToHex(String(value));
        if (hex) picker.value = hex;
      }
    };
    const setChecked = (id: string, value: boolean | undefined) => {
      const el = host.querySelector<HTMLInputElement>(`#${id}`);
      if (!el || value === undefined) return;
      el.checked = !!value;
    };

    // If complex format — extract effects into legacy fields
    if (cfg.complexEffects?.effects) {
      const effects: any[] = cfg.complexEffects.effects;
      for (const e of effects) {
        if (!e.enabled && e.enabled !== undefined) continue;
        switch (e.type) {
          case EffectType.Fill:
          case 'fill':
            if (e.color) setVal('rc-fillColor', e.color);
            break;
          case EffectType.Gradient:
          case 'gradient':
            if (e.config) {
              setChecked('rc-grad-enabled', e.config.enabled !== false);
              if (e.config.angle !== undefined) setVal('rc-grad-angle', e.config.angle);
              if (e.config.offset !== undefined) setVal('rc-grad-offset', e.config.offset);
              const stops = e.config.stops || [];
              if (stops[0]) {
                if (stops[0].color) setVal('rc-grad-stop0-color', stops[0].color);
                if (stops[0].offset !== undefined) setVal('rc-grad-stop0-offset', stops[0].offset);
              }
              if (stops[1]) {
                if (stops[1].color) setVal('rc-grad-stop1-color', stops[1].color);
                if (stops[1].offset !== undefined) setVal('rc-grad-stop1-offset', stops[1].offset);
              }
            }
            break;
          case EffectType.OuterGlow:
          case 'outerGlow':
            if (e.config) {
              setChecked('rc-og-enabled', e.config.enabled !== false);
              if (e.config.color) setVal('rc-og-color', e.config.color as string);
              if (e.config.width !== undefined) setVal('rc-og-width', e.config.width);
              if (e.config.softness !== undefined) setVal('rc-og-softness', e.config.softness);
              if (e.config.opacity !== undefined) setVal('rc-og-opacity', e.config.opacity);
            }
            break;
          case EffectType.InnerGlow:
          case 'innerGlow':
            if (e.config) {
              setChecked('rc-ig-enabled', e.config.enabled !== false);
              if (e.config.color) setVal('rc-ig-color', e.config.color as string);
              if (e.config.width !== undefined) setVal('rc-ig-width', e.config.width);
              if (e.config.softness !== undefined) setVal('rc-ig-softness', e.config.softness);
              if (e.config.opacity !== undefined) setVal('rc-ig-opacity', e.config.opacity);
            }
            break;
          case EffectType.Shadow:
          case 'shadow':
            if (e.config) {
              setChecked('rc-sh-enabled', e.config.enabled !== false);
              if (e.config.color) setVal('rc-sh-color', e.config.color as string);
              if (e.config.offsetX !== undefined) setVal('rc-sh-offsetX', e.config.offsetX);
              if (e.config.offsetY !== undefined) setVal('rc-sh-offsetY', e.config.offsetY);
              if (e.config.softness !== undefined) setVal('rc-sh-softness', e.config.softness);
              if (e.config.opacity !== undefined) setVal('rc-sh-opacity', e.config.opacity);
            }
            break;
        }
      }
      // Globals from complexEffects or top-level
      const ce = cfg.complexEffects;
      if (ce.letterSpacing !== undefined) setVal('rc-letterSpacing', ce.letterSpacing);
      if (ce.lineSpacing !== undefined) setVal('rc-lineSpacing', ce.lineSpacing);
      if (ce.canvasScaleX !== undefined) setVal('rc-scaleX', ce.canvasScaleX);
      if (ce.canvasScaleY !== undefined) setVal('rc-scaleY', ce.canvasScaleY);
    } else {
      // Legacy format directly
      if (cfg.fillColor) setVal('rc-fillColor', cfg.fillColor);
      if (cfg.fillGradient) {
        setChecked('rc-grad-enabled', cfg.fillGradient.enabled !== false);
        if (cfg.fillGradient.angle !== undefined) setVal('rc-grad-angle', cfg.fillGradient.angle);
        if (cfg.fillGradient.offset !== undefined)
          setVal('rc-grad-offset', cfg.fillGradient.offset);
        const stops = cfg.fillGradient.stops || [];
        if (stops[0]) {
          if (stops[0].color) setVal('rc-grad-stop0-color', stops[0].color);
          if (stops[0].offset !== undefined) setVal('rc-grad-stop0-offset', stops[0].offset);
        }
        if (stops[1]) {
          if (stops[1].color) setVal('rc-grad-stop1-color', stops[1].color);
          if (stops[1].offset !== undefined) setVal('rc-grad-stop1-offset', stops[1].offset);
        }
      }
      if (cfg.outerGlow) {
        setChecked('rc-og-enabled', cfg.outerGlow.enabled !== false);
        if (cfg.outerGlow.color) setVal('rc-og-color', cfg.outerGlow.color as string);
        if (cfg.outerGlow.width !== undefined) setVal('rc-og-width', cfg.outerGlow.width);
        if (cfg.outerGlow.softness !== undefined) setVal('rc-og-softness', cfg.outerGlow.softness);
        if (cfg.outerGlow.opacity !== undefined) setVal('rc-og-opacity', cfg.outerGlow.opacity);
      }
      if (cfg.innerGlow) {
        setChecked('rc-ig-enabled', cfg.innerGlow.enabled !== false);
        if (cfg.innerGlow.color) setVal('rc-ig-color', cfg.innerGlow.color as string);
        if (cfg.innerGlow.width !== undefined) setVal('rc-ig-width', cfg.innerGlow.width);
        if (cfg.innerGlow.softness !== undefined) setVal('rc-ig-softness', cfg.innerGlow.softness);
        if (cfg.innerGlow.opacity !== undefined) setVal('rc-ig-opacity', cfg.innerGlow.opacity);
      }
      if (cfg.shadow) {
        setChecked('rc-sh-enabled', cfg.shadow.enabled !== false);
        if (cfg.shadow.color) setVal('rc-sh-color', cfg.shadow.color as string);
        if (cfg.shadow.offsetX !== undefined) setVal('rc-sh-offsetX', cfg.shadow.offsetX);
        if (cfg.shadow.offsetY !== undefined) setVal('rc-sh-offsetY', cfg.shadow.offsetY);
        if (cfg.shadow.softness !== undefined) setVal('rc-sh-softness', cfg.shadow.softness);
        if (cfg.shadow.opacity !== undefined) setVal('rc-sh-opacity', cfg.shadow.opacity);
      }
      if (cfg.letterSpacing !== undefined) setVal('rc-letterSpacing', cfg.letterSpacing);
      if (cfg.lineSpacing !== undefined) setVal('rc-lineSpacing', cfg.lineSpacing);
      if (cfg.canvasScaleX !== undefined) setVal('rc-scaleX', cfg.canvasScaleX);
      if (cfg.canvasScaleY !== undefined) setVal('rc-scaleY', cfg.canvasScaleY);
    }
    // Top-level fields (same in both formats)
    const hasAutoscale = !!(cfg.dimensionSource && cfg.dimensionSourceScale);
    setChecked('rc-autoscale-enabled', hasAutoscale);
    if (cfg.dimensionSource) {
      const ds = host.querySelector<HTMLSelectElement>('#rc-dimension-source');
      if (ds) ds.value = cfg.dimensionSource;
    }
    if (cfg.dimensionSourceScale) {
      const dsc = host.querySelector<HTMLSelectElement>('#rc-dimension-scale');
      if (dsc) dsc.value = cfg.dimensionSourceScale;
    }
    if (cfg.paddingTop !== undefined) setVal('rc-padding-top', cfg.paddingTop);
    if (cfg.paddingRight !== undefined) setVal('rc-padding-right', cfg.paddingRight);
    if (cfg.paddingBottom !== undefined) setVal('rc-padding-bottom', cfg.paddingBottom);
    if (cfg.paddingLeft !== undefined) setVal('rc-padding-left', cfg.paddingLeft);
  }

  private applyFromForm(host: HTMLDivElement): void {
    const selected = this.getSelectedObjects();
    if (selected.length === 0) return;
    const cfg = this.buildRenderConfigFromForm(host);
    for (const obj of selected) {
      this.applyRenderConfig(obj, cfg);
      this.saveToSessionLegacy(obj, cfg);
    }
  }

  private buildRenderConfigFromForm(host: HTMLDivElement): RenderConfig {
    const v = (id: string) => (host.querySelector(`#${id}`) as HTMLInputElement | null)?.value;
    const c = (id: string) => (host.querySelector(`#${id}`) as HTMLInputElement | null)?.checked;
    const cfg: RenderConfig = {};

    const fill = v('rc-fillColor');
    if (fill) cfg.fillColor = fill;

    const gradAny =
      c('rc-grad-enabled') !== undefined || v('rc-grad-angle') || v('rc-grad-stop0-color');
    if (gradAny) {
      const grad: GradientFillConfig = { type: 'linear' };
      grad.enabled = c('rc-grad-enabled') ?? true;
      const ang = v('rc-grad-angle');
      if (ang) grad.angle = parseFloat(ang);
      const gof = v('rc-grad-offset');
      if (gof) grad.offset = Math.max(-1, Math.min(1, parseFloat(gof)));
      const stops: GradientStopConfig[] = [];
      const s0c = v('rc-grad-stop0-color');
      const s0o = v('rc-grad-stop0-offset');
      if (s0c || s0o) stops.push({ color: s0c, offset: s0o ? parseFloat(s0o) : 0 });
      const s1c = v('rc-grad-stop1-color');
      const s1o = v('rc-grad-stop1-offset');
      if (s1c || s1o) stops.push({ color: s1c, offset: s1o ? parseFloat(s1o) : 1 });
      if (stops.length > 0) grad.stops = stops;
      cfg.fillGradient = grad;
    }

    if (v('rc-og-color') || v('rc-og-width') || c('rc-og-enabled') !== undefined) {
      const og: GlowStrokeConfig = {};
      og.enabled = c('rc-og-enabled') ?? true;
      const col = v('rc-og-color');
      if (col) og.color = col;
      const w = v('rc-og-width');
      if (w) og.width = parseFloat(w);
      const sf = v('rc-og-softness');
      if (sf) og.softness = parseFloat(sf);
      const op = v('rc-og-opacity');
      if (op) og.opacity = parseFloat(op);
      cfg.outerGlow = og;
    }

    if (v('rc-ig-color') || v('rc-ig-width') || c('rc-ig-enabled') !== undefined) {
      const ig: GlowStrokeConfig = {};
      ig.enabled = c('rc-ig-enabled') ?? true;
      const col = v('rc-ig-color');
      if (col) ig.color = col;
      const w = v('rc-ig-width');
      if (w) ig.width = parseFloat(w);
      const sf = v('rc-ig-softness');
      if (sf) ig.softness = parseFloat(sf);
      const op = v('rc-ig-opacity');
      if (op) ig.opacity = parseFloat(op);
      cfg.innerGlow = ig;
    }

    if (v('rc-sh-color') || v('rc-sh-offsetX') || c('rc-sh-enabled') !== undefined) {
      const sh: ShadowConfig = {};
      sh.enabled = c('rc-sh-enabled') ?? true;
      const col = v('rc-sh-color');
      if (col) sh.color = col;
      const ox = v('rc-sh-offsetX');
      if (ox) sh.offsetX = parseFloat(ox);
      const oy = v('rc-sh-offsetY');
      if (oy) sh.offsetY = parseFloat(oy);
      const sf = v('rc-sh-softness');
      if (sf) sh.softness = parseFloat(sf);
      const op = v('rc-sh-opacity');
      if (op) sh.opacity = parseFloat(op);
      cfg.shadow = sh;
    }

    const ls = v('rc-letterSpacing');
    if (ls) cfg.letterSpacing = parseFloat(ls);
    const lineSpacing = v('rc-lineSpacing');
    if (lineSpacing) cfg.lineSpacing = parseFloat(lineSpacing);
    const sx = v('rc-scaleX');
    if (sx) cfg.canvasScaleX = parseFloat(sx);
    const sy = v('rc-scaleY');
    if (sy) cfg.canvasScaleY = parseFloat(sy);

    const autoscaleEnabled = c('rc-autoscale-enabled');
    if (autoscaleEnabled) {
      const dsSelect = host.querySelector<HTMLSelectElement>('#rc-dimension-source');
      const dscSelect = host.querySelector<HTMLSelectElement>('#rc-dimension-scale');
      if (dsSelect?.value) cfg.dimensionSource = dsSelect.value;
      if (dscSelect?.value) cfg.dimensionSourceScale = dscSelect.value;
    }

    const pt = v('rc-padding-top');
    if (pt) cfg.paddingTop = parseFloat(pt);
    const pr = v('rc-padding-right');
    if (pr) cfg.paddingRight = parseFloat(pr);
    const pb = v('rc-padding-bottom');
    if (pb) cfg.paddingBottom = parseFloat(pb);
    const pl = v('rc-padding-left');
    if (pl) cfg.paddingLeft = parseFloat(pl);

    return cfg;
  }

  private copyJsonFromForm(host: HTMLDivElement): void {
    const selected = this.getSelectedObjects();
    if (selected.length === 0) return;
    const render = this.buildRenderConfigFromForm(host);
    const scenesMap = new Map<string, { id: string; render: RenderConfig }[]>();
    for (const obj of selected) {
      const sceneId = this.normalizeSceneUrlToSceneId(obj.sceneUrl) || 'scene_id_here';
      if (!scenesMap.has(sceneId)) scenesMap.set(sceneId, []);
      scenesMap.get(sceneId)!.push({ id: obj.id || 'text_id_here', render });
    }
    const payload = {
      scenes: Array.from(scenesMap.entries()).map(([id, items]) => ({ id, items })),
    };
    const json = JSON.stringify(payload, null, 2);
    this.copyToClipboard(json, host);
  }

  private normalizeSceneUrlToSceneId(sceneUrl?: string): string {
    if (!sceneUrl) return '';
    return sceneUrl.replace(/\/+/g, '/').replace(/\//g, '_') + '_object';
  }

  private copyToClipboard(text: string, host: HTMLDivElement): void {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard
        .writeText(text)
        .then(() => this.flashStatus(host, 'Copied JSON to clipboard'))
        .catch(() => this.fallbackCopy(text, host));
    } else {
      this.fallbackCopy(text, host);
    }
  }

  private fallbackCopy(text: string, host: HTMLDivElement): void {
    try {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      this.flashStatus(host, 'Copied JSON to clipboard');
    } catch {
      this.flashStatus(host, 'Copy failed');
    }
  }

  private flashStatus(host: HTMLDivElement, message: string): void {
    const el = document.createElement('div');
    el.textContent = message;
    el.style.color = '#9BE564';
    el.style.marginTop = '8px';
    host.appendChild(el);
    setTimeout(() => el.remove(), 1500);
  }

  private applyRenderConfig(ttf: TtfTextSceneObject, render?: RenderConfig): void {
    const rc: any = {};
    if (render?.fillColor) rc.fillColor = this.parseColor(render.fillColor);
    if (render?.fillGradient) rc.fillGradient = this.parseGradient(render.fillGradient);
    if (render?.outerGlow) rc.outerGlow = this.parseGlow(render.outerGlow);
    if (render?.innerGlow) rc.innerGlow = this.parseGlow(render.innerGlow);
    if (render?.shadow) rc.shadow = this.parseShadow(render.shadow);
    if (typeof render?.letterSpacing === 'number') rc.letterSpacing = render.letterSpacing;
    if (typeof render?.lineSpacing === 'number') rc.lineSpacing = render.lineSpacing;
    if (typeof render?.canvasScaleX === 'number') rc.canvasScaleX = render.canvasScaleX;
    if (typeof render?.canvasScaleY === 'number') rc.canvasScaleY = render.canvasScaleY;
    if (render?.dimensionSource) rc.dimensionSource = render.dimensionSource;
    if (render?.dimensionSourceScale) rc.dimensionSourceScale = render.dimensionSourceScale;
    if (typeof render?.paddingTop === 'number') rc.paddingTop = render.paddingTop;
    if (typeof render?.paddingRight === 'number') rc.paddingRight = render.paddingRight;
    if (typeof render?.paddingBottom === 'number') rc.paddingBottom = render.paddingBottom;
    if (typeof render?.paddingLeft === 'number') rc.paddingLeft = render.paddingLeft;

    if (
      !rc.fillColor &&
      !rc.fillGradient &&
      !rc.outerGlow &&
      !rc.innerGlow &&
      !rc.shadow &&
      rc.letterSpacing === undefined &&
      rc.lineSpacing === undefined &&
      rc.canvasScaleX === undefined &&
      rc.canvasScaleY === undefined &&
      !rc.dimensionSource &&
      !rc.dimensionSourceScale &&
      rc.paddingTop === undefined &&
      rc.paddingRight === undefined &&
      rc.paddingBottom === undefined &&
      rc.paddingLeft === undefined
    ) {
      ttf.setRenderComponent(null);
      return;
    }
    ttf.setRenderComponent(new TtfRenderComponent(rc));
  }

  // --- Session store methods ---

  private getSessionKey(obj: TtfTextSceneObject): string {
    const sceneId = this.normalizeSceneUrlToSceneId(obj.sceneUrl) || 'unknown_scene';
    return `${sceneId}:${obj.id || 'unknown'}`;
  }

  private saveToSessionLegacy(obj: TtfTextSceneObject, renderConfig: RenderConfig): void {
    const key = this.getSessionKey(obj);
    const entry = this.sessionStore.get(key) || {};
    entry.legacy = JSON.parse(JSON.stringify(renderConfig));
    this.sessionStore.set(key, entry);
  }

  private saveToSessionComplex(obj: TtfTextSceneObject, renderConfig: any): void {
    const key = this.getSessionKey(obj);
    const entry = this.sessionStore.get(key) || {};
    entry.complex = JSON.parse(JSON.stringify(renderConfig));
    this.sessionStore.set(key, entry);
  }

  private getSessionEntry(
    obj: TtfTextSceneObject
  ): { legacy?: RenderConfig; complex?: any } | undefined {
    return this.sessionStore.get(this.getSessionKey(obj));
  }

  /** Snapshot current form values into session before switching tabs */
  private snapshotCurrentTab(obj: TtfTextSceneObject): void {
    if (this.currentTab === 'legacy') {
      const host = document.querySelector('#ttf-tab-content') as HTMLDivElement;
      if (host) {
        const cfg = this.buildRenderConfigFromForm(host);
        this.saveToSessionLegacy(obj, cfg);
      }
    } else {
      this.snapshotComplexTab(obj);
    }
  }

  private snapshotComplexTab(obj: TtfTextSceneObject): void {
    const sessionRender: any = {
      complexEffects: {
        effects: JSON.parse(JSON.stringify(this.currentEffects)),
        letterSpacing: this.getComplexGlobalValue('complex-letterSpacing'),
        lineSpacing: this.getComplexGlobalValue('complex-lineSpacing'),
        canvasScaleX: this.getComplexGlobalValue('complex-scaleX'),
        canvasScaleY: this.getComplexGlobalValue('complex-scaleY'),
      },
    };
    const autoscaleEnabled = (
      document.querySelector('#complex-autoscale-enabled') as HTMLInputElement
    )?.checked;
    if (autoscaleEnabled) {
      const dsSelect = document.querySelector<HTMLSelectElement>('#complex-dimension-source');
      const dscSelect = document.querySelector<HTMLSelectElement>('#complex-dimension-scale');
      if (dsSelect?.value) sessionRender.dimensionSource = dsSelect.value;
      if (dscSelect?.value) sessionRender.dimensionSourceScale = dscSelect.value;
    }
    const pT = this.getComplexGlobalValue('complex-padding-top');
    if (pT !== undefined) sessionRender.paddingTop = pT;
    const pR = this.getComplexGlobalValue('complex-padding-right');
    if (pR !== undefined) sessionRender.paddingRight = pR;
    const pB = this.getComplexGlobalValue('complex-padding-bottom');
    if (pB !== undefined) sessionRender.paddingBottom = pB;
    const pL = this.getComplexGlobalValue('complex-padding-left');
    if (pL !== undefined) sessionRender.paddingLeft = pL;
    this.saveToSessionComplex(obj, sessionRender);
  }

  private buildSessionPayload(): any {
    const scenesMap = new Map<string, { id: string; render: any }[]>();
    this.sessionStore.forEach((entry, key) => {
      const sep = key.indexOf(':');
      const sceneId = key.substring(0, sep);
      const ttfId = key.substring(sep + 1);
      if (!scenesMap.has(sceneId)) scenesMap.set(sceneId, []);
      const render: any = {};
      if (entry.legacy) Object.assign(render, entry.legacy);
      if (entry.complex) Object.assign(render, entry.complex);
      scenesMap.get(sceneId)!.push({ id: ttfId, render });
    });
    return { scenes: Array.from(scenesMap.entries()).map(([id, items]) => ({ id, items })) };
  }

  private logSessionToConsole(): void {
    const payload = this.buildSessionPayload();
    console.log('[TTF Editor] Session store:', JSON.stringify(payload, null, 2));
  }

  private clearSession(): void {
    this.sessionStore.clear();
  }

  // --- Color utilities ---

  private parseGlow(g: GlowStrokeConfig) {
    return {
      enabled: g.enabled !== false,
      color: g.color ? this.parseColor(this.convertColorToString(g.color) || '#ffffff') : undefined,
      width: g.width,
      softness: g.softness,
      opacity: g.opacity,
    };
  }

  private parseShadow(s: ShadowConfig) {
    return {
      enabled: s.enabled !== false,
      color: s.color
        ? this.parseColor(this.convertColorToString(s.color) || 'rgba(0,0,0,0.5)')
        : undefined,
      offsetX: s.offsetX,
      offsetY: s.offsetY,
      softness: s.softness,
      opacity: s.opacity,
    };
  }

  private parseColor(input: string): Color4 {
    const t = input.trim();
    if (t.startsWith('#') || /^[0-9a-fA-F]+$/.test(t)) {
      const hex = t.startsWith('#') ? t.slice(1) : t;
      if (hex.length === 6) {
        return new Color4(
          parseInt(hex.substring(0, 2), 16) / 255,
          parseInt(hex.substring(2, 4), 16) / 255,
          parseInt(hex.substring(4, 6), 16) / 255,
          1
        );
      }
      if (hex.length === 3) {
        return new Color4(
          parseInt(hex[0] + hex[0], 16) / 255,
          parseInt(hex[1] + hex[1], 16) / 255,
          parseInt(hex[2] + hex[2], 16) / 255,
          1
        );
      }
      if (hex.length === 8) {
        return new Color4(
          parseInt(hex.substring(0, 2), 16) / 255,
          parseInt(hex.substring(2, 4), 16) / 255,
          parseInt(hex.substring(4, 6), 16) / 255,
          parseInt(hex.substring(6, 8), 16) / 255
        );
      }
    }
    const rgba = /rgba?\(([^)]+)\)/.exec(t);
    if (rgba) {
      const parts = rgba[1].split(',').map((p) => parseFloat(p.trim()));
      return new Color4(
        (parts[0] ?? 255) / 255,
        (parts[1] ?? 255) / 255,
        (parts[2] ?? 255) / 255,
        parts[3] ?? 1
      );
    }
    return new Color4(1, 1, 1, 1);
  }

  private color4ToHex6(c: Color4): string {
    const r = Math.max(0, Math.min(255, Math.round(c.r * 255)));
    const g = Math.max(0, Math.min(255, Math.round(c.g * 255)));
    const b = Math.max(0, Math.min(255, Math.round(c.b * 255)));
    const to2 = (n: number) => n.toString(16).padStart(2, '0');
    return `#${to2(r)}${to2(g)}${to2(b)}`;
  }

  private colorToHex(input: string): string | null {
    try {
      return this.color4ToHex6(this.parseColor(input));
    } catch {
      return null;
    }
  }

  private parseGradient(g: GradientFillConfig) {
    return {
      enabled: g.enabled !== false,
      type: 'linear' as const,
      angle: g.angle,
      offset: g.offset,
      stops: (g.stops || [])
        .filter((s) => s && (s.color || s.offset !== undefined))
        .map((s) => ({
          offset: s.offset ?? 0,
          color: s.color ? this.parseColor(s.color) : new Color4(1, 1, 1, 1),
        })),
    };
  }
}
